import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import {Backdrop,  Box, Checkbox, CircularProgress, FormControlLabel, IconButton, Tooltip} from "@mui/material";

import { FilterAlt, ViewComfySharp, EditNote, AccountTreeSharp, Transform, HelpOutline } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { blue, lightBlue } from "@mui/material/colors";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import "@plannel/scss/components/DpWorkbench.scss";
import DimensAttrModal from "./dp-workbench-modal/DimensAttrModal";

import AggregationIcon from "@plannel/assets/icons/aggregation.svg";
import AscIcon from "@plannel/assets/icons/asc.svg";
import DescIcon from "@plannel/assets/icons/desc.svg";
import { AggregationGifIcon } from "@plannel/components/AggregationIcon.js";
import PaginationContainer from "@plannel/components/PaginationContainer";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import Dialog from "@plannel/components/Dialog";
import SplitterContainer from "@plannel/components/SplitterContainer";
import {SaveButton, SettingsButton, ResetButton, CheckButton, FilterButton} from "@plannel/components/ActionIconButton";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import BucketToggleButton from "@plannel/components/BucketToggleButton"

import demandValService from "@plannel/services/data/demand-val-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import postnService from "@plannel/services/data/postn-service";
import p13nService from "@plannel/services/system/p13n-service";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import colorUtil from "@plannel/pages/utils/color-util";
import { AbcXyzChipInline } from "@plannel/components/aggrid/AbcXyzChip";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { cloneDeep, isArray, isEqual, isNull, isUndefined, size, uniq, uniqWith, isEmpty, sortBy, includes, isString, entries, find } from "lodash";
import { addDays, format as formatDateFns } from "date-fns";
import LayoutListModal from "./dp-workbench-modal/LayoutListModal";
import ChartConfigModal from "./dp-workbench-modal/ChartConfigModal";
import MeasureModal from "./dp-workbench-modal/DPMeasureModal";
import BulkValueUpdateModal from "./dp-workbench-modal/BulkValueUpdateModal";
import { DPFilterModal } from "./dp-workbench-modal/FilterModal";
import NotesModal from "./dp-workbench-modal/NotesModal";

import DpVersionSelect from "./DpVersionSelect";
import DpExcelButtons from "./dp-workbench-modal/DpExcelButtons";
import companyService from "@plannel/services/data/company-service";
import HrchyGroupModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/HrchyGroupModal";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import WorkbenchEditable from "@plannel/pages/demand-plan/dp-workbench/WorkbenchEditable";

import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { DpDefaultGridSetting } from "@plannel/components/aggrid/DefaultGridSetting";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import FilterStatusPanel, { getFilterStore, extractFilterInfo } from  "@plannel/components/aggrid/FilterStatusPanel";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import planConfigService from "@plannel/services/data/plan-config-service";

const CURRENT_DATE = new ZDate();
const DEFAULT_END_DATE = new ZDate(CURRENT_DATE.getFullYear() + 1, CURRENT_DATE.getMonth() - 1, CURRENT_DATE.getDate(), 23, 59, 59);
const MIN_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1, 0, 0, 0);
const MAX_START_DATE = new ZDate(CURRENT_DATE.getFullYear() + 9, CURRENT_DATE.getMonth(), 0, 23, 59, 59);
const MAX_END_DATE = new ZDate(CURRENT_DATE.getFullYear() + 9, CURRENT_DATE.getMonth() + 1, 0, 23, 59, 59);

const { CUST_LEVEL_ID, CUST_LEVEL, CUST_CD, CUST_CD_ID, CUSTOMER_ATTR,
  ITEM, PROD_LEVEL, PROD_LEVEL_ID, ITEM_CD, ITEM_CD_ID, PROD_ATTR, INV_CLASS_CD  } = WorkbenchUtil.workbenchCds;
const DISPLAY_COLUMN = "display_";
const ATTR_COLUMN = "attr";

/*
 * 연속된 requestReload() 를 한 발로 합칠 때 기다리는 시간. 한 동작이 만드는 상태 갱신은
 * 서너 커밋에 걸치지만 모두 같은 프레임 안에서 끝나므로 이 정도면 충분하고, 사용자에게는
 * 체감되지 않는다. 사용처는 [reloadNonce] 이펙트 하나뿐이다.
 */
const RELOAD_COALESCE_MS = 50;

/*
 * 채널 재고 시뮬레이션 수식 지표(INV_BOH_QTY 등) — z_measure 테이블에 행이 없어(계산만 되는 파생
 * 측정치) ChartConfigModal 이 쓰는 measureService.getAll 목록에는 절대 안 잡힌다. 조회 결과의
 * measureDict(=measure state)에서 뽑아 ChartConfigModal 에 별도로 넘겨 차트 선택 목록에 포함시킨다.
 * DAYS_TYPE 단위(TARGET_INV_DAYS 등)는 차트의 AMT/QTY 토글과 맞지 않아 제외한다.
 */
const CHART_ONLY_CHANNEL_MEASURE_CDS = ["INV_BOH_QTY", "INV_EOH_QTY", "TARGET_INV_QTY_EOH", "SUGGESTED_MP_QTY", "MF_VS_SUGGESTED_QTY"];

/*
 * Advanced Filter 가 걸렸을 때 BF measure 이름 옆에 "?" 를 띄우고, "?" 위에 hover 하면 안내 말풍선을 연다.
 */
const BfInfoCellRenderer = ({ value, valueFormatted, data, context, t }) => {
  const label = valueFormatted ?? value;
  if (data?.resultRow?.measureCd !== "BF_QTY" || !context?.hasAdvancedFilter) {
    return label;
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <span>{label}</span>
      <Tooltip title={t("MSG_BfAdvancedFilterSumTooltip")} arrow placement="right">
        <HelpOutline
          fontSize="small"
          sx={{ fontSize: 16, cursor: "help", color: "text.secondary" }}
        />
      </Tooltip>
    </Box>
  );
};

/* onApply 페이로드는 트리 그대로일 수도, `{advancedFilter, custLevelCd, …}` 로 래핑돼 올 수도 있다. */
const getAdvancedFilterTree = (filters) => filters?.advancedFilter ?? filters?.filter ?? filters ?? null;

const hasAdvancedFilterLevel = (filters) => Boolean(filters?.itemLevelCd || filters?.custLevelCd);

const hasAdvancedFilterChildren = (filters) => {
  const filterTree = getAdvancedFilterTree(filters);
  const hasLevelSelection = hasAdvancedFilterLevel(filters);

  const collectLeaves = (node) => {
    if (!node) {
      return [];
    }
    if (Array.isArray(node.children)) {
      return node.children.flatMap(collectLeaves);
    }
    return [node];
  };

  const leaves = collectLeaves(filterTree);
  return hasLevelSelection || leaves.some(leaf => {
    const filterKey = leaf?.filterName ?? leaf?.headerName;
    if (!filterKey || filterKey === "baseDate") {
      return false;
    }

    return !isExcludeEosNode(leaf) || isActiveExcludeEosNode(leaf);
  });
};

const EXCLUDE_EOS = "excludeEos";

const isExcludeEosNode = (node) => !Array.isArray(node?.children)
  && (node?.filterName ?? node?.headerName) === EXCLUDE_EOS;

const isActiveExcludeEosNode = (node) => isExcludeEosNode(node) && node?.values?.[0] === "Y";

/* 값 'N'(inactive) 은 "EOS 를 제외하지 않는다" 이므로 상단 체크박스를 끈 것과 같게 본다. */
const getExcludeEosFromFilters = (filters) => {
  const hasActiveNode = (node) => {
    if (!node) {
      return false;
    }
    if (Array.isArray(node.children)) {
      return node.children.some(hasActiveNode);
    }
    return isActiveExcludeEosNode(node);
  };

  return hasActiveNode(getAdvancedFilterTree(filters));
};

const stripExcludeEosNodes = (node) => {
  if (!node) {
    return null;
  }
  if (!Array.isArray(node.children)) {
    return isExcludeEosNode(node) ? null : node;
  }

  return { ...node, children: node.children.map(stripExcludeEosNodes).filter(child => child !== null) };
};

/*
 * 조회 요청에 실제로 advancedFilters 가 실리는지(=서버가 필터링된 합계를 내는지).
 *   baseDate 만 있거나 Exclude EOS 만 활성인 경우는 EOS 노드를 뗀 뒤 조건이 남지 않아 보내지 않는다(조회 파라미터 구성부와 같은 기준).
 */
const isAdvancedFilterSent = (filters) => hasAdvancedFilterChildren(stripExcludeEosNodes(getAdvancedFilterTree(filters)));

const BASE_DATE = "baseDate";
/* AdvancedFilter 에 넘기는 잠긴 조건 — 렌더마다 새 배열을 만들지 않도록 모듈 상수로 둔다. */
const ADVANCED_FILTER_LOCKED_NAMES = [BASE_DATE];

/*
 * baseDate 노드 값은 반드시 `yyyy-MM-dd` 문자열로 저장한다.
 *   Date 를 그대로 두면 Confirm 의 JSON 직렬화에서 UTC ISO 문자열이 되어(KST 2027-05-01 → "2027-04-30T15:00:00.000Z")
 *   요약 칩(앞 7자리 절단)과 조회 파라미터가 한 달 앞으로 밀린다.
 */
const toDateOnlyString = (value) => {
  const date = toLocalDate(value);
  return isNaN(date) ? value : formatDateFns(date, "yyyy-MM-dd");
};

const toLocalDate = (value) => {
  if (value instanceof Date) {
    return value;
  }

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  return dateOnly
    ? new ZDate(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 0, 0, 0)
    : new ZDate(value);
};

const getAdvancedFilterBaseDateRange = (filters) => {
  const baseDateNode = (getAdvancedFilterTree(filters)?.children ?? [])
    .find(node => !Array.isArray(node?.children) && (node?.headerName ?? node?.filterName) === BASE_DATE);

  if (!baseDateNode || size(baseDateNode.values) < 2) {
    return null;
  }

  const startDate = toLocalDate(baseDateNode.values[0]);
  const endDate = toLocalDate(baseDateNode.values[1]);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  return { startDate, endDate };
};

/*
 * 적용된 트리의 baseDate 노드를 상단 기간·버킷(filterStartDate/filterEndDate/selectTD)에 맞춘다.
 *   Confirm 시 상단 기간은 baseDate 로 맞춰지고, 이후 버킷 전환은 상단 기간만 다시 계산한다.
 *   트리의 baseDate 는 Confirm 스냅샷이라 그대로 보내면 주 버킷인데도 월 기간으로 조회됐다(TC-41).
 */
const syncAdvancedFilterBaseDate = (tree, startDate, endDate, timeDimension) => {
  if (!Array.isArray(tree?.children)) {
    return tree;
  }
  const hasRange = Boolean(startDate && endDate);

  return {
    ...tree,
    children: tree.children.map(node =>
      !Array.isArray(node?.children) && (node?.headerName ?? node?.filterName) === BASE_DATE
        ? {
          ...node,
          filterUnit: timeDimension ?? node.filterUnit,
          ...(hasRange ? { values: [toDateOnlyString(startDate), toDateOnlyString(endDate)] } : {})
        }
        : node
    )
  };
};

const Workbench = ({ t, title, viewName }) => {
  const reduxViewState = cloneDeep(reduxUtil.getViewState(viewName)) || {};
  const reduxDispatch = useDispatch();

  const isChannel = viewName === "DP_CHANNEL_WORKBENCH"; //channel workbench
  const DP_WORKBENCH_GRID = isChannel ? "DP_CHANNEL_WORKBENCH_GRID" : "DP_WORKBENCH_GRID"

  const { customer, item, dpP13N, muicolor, dpVersion, buckets, dpABCConfig, filterType, versionStatusCode } = codeUtil;
  const { DP_WORKBENCH_ORDER_ITEM, DP_CHANNEL_WORKBENCH_ORDER_ITEM, DP_CHANNEL_WORKBENCH_ETC, DP_WORKBENCH_ETC } = dpP13N;
  const pageCd = isChannel ? DP_CHANNEL_WORKBENCH_ETC : DP_WORKBENCH_ETC;

  const defaultTimeDimension = buckets.MONTH;
  const { snackBarMessageColor } = colorUtil;
  const startDay = useRef(reduxViewState?.startDay ?? null);
  const sortType = "asc";
  const QTY = "QTY";
  const AMT = "AMT";

  const [attrConfig, setAttrConfig] = useState(null);
  const drill = useRef(false);
  const isLevel = useRef(false);
  const gridRef = useRef();
  const responseRef = useRef();
  /*
   * demandval 조회 순번 — **늦게 도착한 이전 조회 응답으로 화면을 덮지 않기 위해** 센다.
   *   결과가 오기 전에 조건을 바꾸면(특히 advanced filter 의 remove all) 조회가 겹치는데,
   *   먼저 던진 쪽이 나중에 도착하면 방금 지운 조건의 결과("no data")가 화면에 남았다.
   */
  const retrieveSeqRef = useRef(0);

  const [isLoading, setIsLoading] = useState(false);
  const [workbench, setWorkbench] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [dimenModalOpen, setDimenModalOpen] = useState(false);
  const [dimenSelectLv, setDimenSelectLv] = useState({});
  const [chartP13nData, setChartP13nData] = useState({});
  const displayNameRef = useRef({});
  const [chartModalResultData, setChartModalResultData] = useState({});
  const [dimensionAttrData, setDimensionAttrData] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(reduxViewState?.startDate ? new ZDate(reduxViewState.startDate) : CURRENT_DATE);
  const [filterEndDate, setFilterEndDate] = useState(reduxViewState?.endDate ? new ZDate(reduxViewState.endDate) : DEFAULT_END_DATE);
  const [editableHeaderSet, setEditableHeaderSet] = useState([]);
  const editableMeasure = useRef([]);
  const enabledChartMeasure = useRef([]);
  /*
   * 지금 화면에 그려진 데이터가 **실제로 어떤 레벨로 집계된 것인지**(서버가 응답에 실어 준 값).
   *   상단 필터의 selectCL/selectIL 과 다를 수 있다 — getLevelCdOnParams 가
   *   Customer/Item Hierarchy 필터값이 있으면 조회를 그 계층 레벨로 승격시키기 때문이다.
   *   저장(custConfigCd/itemConfigCd)은 화면에 그려진 행의 레벨을 따라가야 하므로 이 값을 쓴다.
   */
  const resultCustLevelCd = useRef(null);
  const resultItemLevelCd = useRef(null);
  /*redux Drill : RESET BUTTON OR VERSION 변경시 풀림*/
  const isDrillSingleMeasure = useRef(reduxViewState?.isDrillSingleMeasure ?? false);
  const drillMeasure = useRef(reduxViewState?.drillMeasure);

  const [filterEl, setFilterEl] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hrchyGroupOpen, setHrchyGroupOpen] = useState(false);

  const [modalData, setModalData] = useState({});
  const [layoutModalData, setLayoutModalData] = useState(reduxViewState?.layoutModalData ?? {});
  const [planningRange, setPlanningRange] = useState(12);
  const [bulkValueUpdateModalOpen, setBulkValueUpdateModalOpen] = useState(false);
  const [bulkValueNodeData, setBulkValueNodeData] = useState({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [measure, setMeasure] = useState({});
  const [pivotData, setPivotData] = useState({});
  const [pivotDataCopy, setPivotDataCopy] = useState({});
  const [rowPivotData, setRowPivotData] = useState([]);
  const [workbenchNoteOpen, setWorkbenchNoteOpen] = useState(true);
  const [workbenchCellData, setWorkbenchCellData] = useState({});
  const [selectVersion, setSelectVersion] = useState(reduxViewState?.selectVersion ?? {});
  const selectTD = useRef(reduxViewState?.timeDimension ?? selectVersion?.planConfigObj?.BUCKET_UNIT ?? defaultTimeDimension);
  const [vListReload, setVListReload] = useState(false);
  const [translationKey, setTranslationKey] = useState({});
  const [chartConfigOpen, setChartConfigOpen] = useState(false);
  const [custLevel, setCustLevel] = useState(reduxViewState?.custLevelList ?? [customer]);
  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevelList ?? [item]);
  const [custGrpLevel, setCustGrpLevel] = useState(reduxViewState?.custGrpLevelList ?? []);
  const [itemGrpLevel, setItemGrpLevel] = useState(reduxViewState?.itemGrpLevelList ?? []);
  const [grpLevel, setGrpLevel] = useState(reduxViewState?.grpLevelList ?? []);
  const [custFilter, setCustFilter] = useState(reduxViewState?.custFilter ?? []);
  const [itemFilter, setItemFilter] = useState(reduxViewState?.itemFilter ?? []);
  const [custFilterType, setCustFilterType] = useState(reduxViewState?.custFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [disabledInvClass, setDisabledInvClass] = useState(false);
  const [selectCL, setSelectCL] = useState(reduxViewState?.custLevelCd ?? "");
  const [selectIL, setSelectIL] = useState(reduxViewState?.itemLevelCd ?? "");
  const [selectCHG, setSelectCHG] = useState(reduxViewState?.custGrpLevelCd ?? "");
  const [selectIHG, setSelectIHG] = useState(reduxViewState?.itemGrpLevelCd ?? "");
  const [selectTDCombo, setSelectTDCombo] = useState(selectTD.current);
  const [orderByItem, setOrderByItem] = useState(reduxViewState?.orderByItem ?? null);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

   const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ?? null);
   const [advancedFilterColor, setAdvancedFilterColor] = useState(hasAdvancedFilterChildren(reduxViewState?.advancedFilters) ? "info" : "default");
   const advancedFilterOwnsLevel = useRef(hasAdvancedFilterLevel(reduxViewState?.advancedFilters));
   /*
    * 고급 필터가 켜진 상태에서 drill / 레벨 이동으로 바뀐 그리드 레벨 { custLevelCd, itemLevelCd }.
    *   요청 파라미터는 고급 필터의 레벨로 덮어써지므로, 이 값이 있으면 그 위에 다시 덮는다.
    *   drill 이 정한 계층 범위(custFilter/itemFilter, customerFilterList/itemFilterList)도 이 값이 있을 때만 함께 보낸다.
    *   고급 필터가 바뀌면(적용·전체 삭제) 비운다.
    */
   const advancedFilterDrillLevel = useRef(null);
   const [openFilter, setOpenFilter] = useState(false);
   const prevOpenFilterRef = useRef(false);
   /*
    * 패널을 연 시점의 일반 필터 스냅샷. AdvancedFilter 는 initialConditions 의 참조가 바뀔 때마다 트리를 다시 심으므로
    * 매 렌더 새로 계산되는 값을 그대로 넘기면 편집 중 트리가 덮어써진다. 그래서 열 때 한 번만 고정하고,
    * Confirm / Remove all / 닫기 시점에 비운다(이후엔 고급 필터 트리가 원본).
    */
   const [advancedFilterInitialConditions, setAdvancedFilterInitialConditions] = useState(null);

  const [saveTarget, setSaveTarget] = useState([]);
  const [formatGridHeader, setFormatGridHeader] = useState([]);
  const weekFormatHeader = useRef();
  const reverseHeader = useRef();

  const pageSizes = [20, 50, 100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [totalRows, setTotalRows] = useState(0);

  const [layoutId, setLayoutId] = useState(null);
  const [appliedLayout, setAppliedLayout] = useState(reduxViewState?.layoutId ?? null);
  const [layoutOpen, setLayoutOpen] = useState(false);

  const [reload, setReload] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [finishSave, setFinishSave] = useState(false);

  const [disabledSave, setDisabledSave] = useState(reduxViewState?.disabledSave ?? false);
  const statusReady = useRef(false);
  const accessControl = useRef(false);
  const approvalAccessControl = useRef(false);
  const retrieveInFlightRef = useRef(false);
  const queuedReloadRef = useRef(false);
  const [noteUpdateFlg, setNoteUpdateFlg] = useState(false);
  const [updateNoteData, setUpdateNoteData] = useState({});

  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [customerFilterList, setCustomerFilterList] = useState(reduxViewState?.customerFilterList ?? {});
  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [customerFilterColor, setCustomerFilterColor] = useState(size(reduxViewState?.customerFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);

  const [cellInfo, setCellInfo] = useState(reduxViewState?.cellInfo ?? {});
  const myPostn = useRef();

  const editableColor = lightBlue[100];
  const [modelCode, setModelCode] = useState(customer.name);

  const editableHeaderIdx = useRef();
  const dataMap = useRef({});

  const requestReload = useCallback(() => {
    setReload(true);
    if (retrieveInFlightRef.current) {
      queuedReloadRef.current = true;
      return;
    }
    setReloadNonce(prev => prev + 1);
  }, []);
  const [reduxObjWorkbench, setReduxObjWorkbench] = useState({});

  const reduxState = reduxUtil.createObjectState(viewName);


  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [treeload, setTreeload] = useState(false);
  const [itemTreeLoad, setItemTreeLoad] = useState(false);
  const [defaultValue] = useState([]);
  const [customValue, setCustomValue] = useState([]);
  const [itemCustomValue, setItemCustomValue] = useState([]);
  const [sortParams, setSortParams] = useState(reduxViewState?.sortParams ?? {});
  const workbenchThreshold = useRef({});
  const [excludeEos, setExcludeEos] = useState(reduxViewState?.excludeEos ?? false);

  const [itemHrchyDefaultValue] = useState([]);
  const defaultGridSettingMemo = useMemo(() => {
    return DpDefaultGridSetting({ title, viewName: DP_WORKBENCH_GRID})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const userData = JSON.parse(localStorage.getItem("user"));

  const [selectedHrchyNum, setSelectedHrchyNum] = useState(reduxViewState?.custGrpLevelList ? reduxViewState?.custGrpLevelList.filter(
    custGrp => custGrp.grpCd === reduxViewState?.custGrpLevelCd)[0]?.hrchyNum : codeUtil.hrchyNum.HRCHY);
  const [selectedItemHrchyNum, setSelectedItemHrchyNum] = useState(reduxViewState?.itemGrpLevelList ? reduxViewState?.itemGrpLevelList.filter(
    itemGrp => itemGrp.grpCd === reduxViewState?.itemGrpLevelCd)[0]?.hrchyNum : codeUtil.hrchyNum.HRCHY);

  const advancedFilterColumnDefs = useMemo(() => {
    const createAttributeColumns = (selectedAttrs, scope, groupLabel, configMap) => Array.from({ length: 10 }, (_, idx) => {
      const attrCd = selectedAttrs[idx];
      const attrOrder = idx + 1;
      const scopedAttrName = `${scope}Attr${attrOrder}`;

      return {
        // headerName: scopedAttrName,
        headerName: attrCd ? (configMap?.[attrCd] ?? attrCd) : `attr${attrOrder}`,
        groupLabel,
        filterName: scopedAttrName,
        filterScope: scope,
        filterType: "string"
      };
    });

    const attributeNames = ["attr01", "attr02", "attr03", "attr04", "attr05", "attr06", "attr07", "attr08", "attr09", "attr10"];
    const customerAttributeColumns = createAttributeColumns(attributeNames, "customer", `${t("customer")} ${t("attr")}`, attrConfig?.customer);
    const itemAttributeColumns = createAttributeColumns(attributeNames, "item", `${t("item")} ${t("attr")}`, attrConfig?.item);

    return [
      { headerName: "customerHrchyCd", filterName: "customerHrchyCd", filterType: "string" },
      { headerName: "customerHrchyName", filterName: "customerHrchyName", filterType: "string" },
      { headerName: "itemHrchyCd", filterName: "itemHrchyCd", filterType: "string" },
      { headerName: "itemHrchyNm", filterName: "itemHrchyNm", filterType: "string" },
      { headerName: ITEM_CD, filterName: "itemCd", filterType: "string" },
      { headerName: "itemName", filterName: "itemName", filterType: "string" },
      { headerName: CUST_CD, filterName: "customerCd", filterType: "string" },
      { headerName: "customerName", filterName: "customerName", filterType: "string" },
      /* 주 버킷 달력은 상단 기간과 같은 규칙 — 시작일은 주 시작 요일, 종료일은 주 마지막 요일만 고른다. */
      {
        headerName: "baseDt", filterName: "baseDate", filterType: "date", filterUnit: selectTDCombo,
        shouldDisableStartDate: e => WorkbenchUtil.ableDayOfWeek(e, selectTD.current, startDay.current),
        shouldDisableEndDate: e => WorkbenchUtil.ableEndDayOfWeek(e, selectTD.current, startDay.current)
      },
      { headerName: INV_CLASS_CD, filterName: "invClassCd", filterType: "select", filterOption: dpABCConfig ?? [] },
      /* 컬럼 비교가 아니라 조회 플래그다 — 값(Y/N)만 꺼내 params.excludeEos 로 보낸다(EXCLUDE_EOS 주석). */
      { headerName: EXCLUDE_EOS, filterName: EXCLUDE_EOS, filterType: "boolean" },
      ...customerAttributeColumns,
      ...itemAttributeColumns,
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dpABCConfig, CUSTOMER_ATTR, PROD_ATTR, dimensionAttrData, selectCHG, selectIHG, attrConfig, selectTDCombo]);

  /*
   * 현재 적용된 일반 필터(계층/속성/ABC-XYZ/EOS)를 Advanced Filter 트리 조건으로 변환한다.
   *   고급 필터를 적용하면 아래 [advancedFilters] 효과가 일반 필터를 모두 비우므로,
   *   이 변환이 일반 필터 → 고급 필터 트리로 넘어가는 유일한 다리다. 빠지면 계층 필터가 조용히 사라진다.
   *   값은 패널을 여는 순간에만 스냅샷(advancedFilterInitialConditions)으로 떠서 넘긴다 — 아래 openFilter 효과 참고.
   */
  const advancedFilterLegacyConditions = useMemo(() => {
    if (hasAdvancedFilterChildren(advancedFilters)) {
      return null;
    }

    const normalizeScopedAttrFilterName = (filterName, scope) => {
      if (!scope || !isString(filterName)) {
        return filterName;
      }

      const normalizedScope = scope.toLowerCase();
      const plainAttrMatched = /^attr(\d{1,2})$/i.exec(filterName);
      if (plainAttrMatched) {
        return `${normalizedScope}Attr${Number(plainAttrMatched[1])}`;
      }

      const scopedAttrMatched = /^(customer|item)Attr(\d{1,2})$/i.exec(filterName);
      if (scopedAttrMatched) {
        return `${scopedAttrMatched[1].toLowerCase()}Attr${Number(scopedAttrMatched[2])}`;
      }

      return filterName;
    };

    // advancedFilterColumnDefs에서 filterName+scope로 headerName 조회
    const findHeader = (filterName, scope) => {
      const normalizedFilterName = normalizeScopedAttrFilterName(filterName, scope);
      const col = advancedFilterColumnDefs.find(c =>
        c.filterName === normalizedFilterName && (!scope || !c.filterScope || c.filterScope === scope)
      );

      // AdvancedFilter 노드의 headerName은 백엔드 바인딩 키(DPColumnEnum)와 동일해야 한다.
      return col?.filterName ?? normalizedFilterName;
    };

    const getOperator = (filterTypeArr) => filterTypeArr?.[1] === filterType.notEqual?.cd ? 'notEqual' : 'equals';
    const getLogic   = (filterTypeArr) => filterTypeArr?.[0] === filterType.and?.cd ? 'AND' : 'OR';

    const children = [];

    if (filterStartDate && filterEndDate) {
      children.push({
        filterType: "date", headerName: "baseDate", filterName: "baseDate",
        operator: "inRange", filterUnit: selectTDCombo,
        /* Date 그대로면 Confirm 직렬화에서 UTC ISO 문자열이 되어 날짜가 밀린다 → yyyy-MM-dd 로 저장 */
        values: [toDateOnlyString(filterStartDate), toDateOnlyString(filterEndDate)]
      });
    }

    // 2. 거래처 계층 트리 선택 (filterValue.customerHrchys)
    const customerHrchys = filterValue?.customerHrchys ?? [];
    if (customerHrchys.length > 0 && custFilter.length === 0) {
      const hdr = findHeader("customerHrchyCd");
      const items = customerHrchys.map(h => ({
        filterType: "string", headerName: hdr, filterName: "customerHrchyCd", operator: "equals", values: [h.hrchyCd]
      }));
      children.push(items.length === 1 ? items[0] : { logicOperator: getLogic(custFilterType), children: items });
    }

    // 3. 품목 계층 트리 선택 (filterValue.category)
    const itemHrchys = filterValue?.category ?? [];
    if (itemHrchys.length > 0 && itemFilter.length === 0) {
      const hdr = findHeader("itemHrchyCd");
      const items = itemHrchys.map(h => ({
        filterType: "string", headerName: hdr, filterName: "itemHrchyCd", operator: "equals", values: [h.hrchyCd]
      }));
      children.push(items.length === 1 ? items[0] : { logicOperator: getLogic(itemFilterType), children: items });
    }

    // 4. custFilter (계층 레벨 코드 배열)
    if (custFilter.length > 0) {
      const hdr = findHeader("customerHrchyCd");
      const items = custFilter.map(code => ({
        filterType: "string", headerName: hdr, filterName: "customerHrchyCd", operator: "equals", values: [code]
      }));
      children.push(items.length === 1 ? items[0] : { logicOperator: getLogic(custFilterType), children: items });
    }

    // 5. itemFilter (계층 레벨 코드 배열)
    if (itemFilter.length > 0) {
      const hdr = findHeader("itemHrchyCd");
      const items = itemFilter.map(code => ({
        filterType: "string", headerName: hdr, filterName: "itemHrchyCd", operator: "equals", values: [code]
      }));
      children.push(items.length === 1 ? items[0] : { logicOperator: getLogic(itemFilterType), children: items });
    }

    // 8. invClassCdList (ABC-XYZ)
    if (invClassCdList.length > 0) {
      const invClassHdr = findHeader("invClassCd");
      const invClassFilterOption = dpABCConfig ?? [];
      const items = invClassCdList.map(code => ({
        filterType: "select", headerName: invClassHdr, filterName: "invClassCd",
        operator: "equals", filterOption: invClassFilterOption, values: [code]
      }));
      children.push(items.length === 1 ? items[0] : { logicOperator: 'OR', children: items });
    }

    // 9. excludeEos (상단 EOS 제외 체크박스 → boolean 조건 1개)
    if (excludeEos) {
      children.push({
        filterType: "boolean", headerName: findHeader(EXCLUDE_EOS), filterName: EXCLUDE_EOS,
        operator: "equals", values: ["Y"]
      });
    }

    // 6. customerFilterList (필터 모달 - 거래처 속성 필터)
    if (size(customerFilterList) > 0) {
      const op = getOperator(custFilterType);
      const logicOp = getLogic(custFilterType);
      const filterItems = Object.entries(customerFilterList).flatMap(([key, values]) =>
        (values ?? []).map(val => {
          const filterName = key === 'name' ? 'customerName' : normalizeScopedAttrFilterName(key, "customer");
          const hdr = findHeader(filterName, "customer");
          return { filterType: "string", headerName: hdr, filterName, operator: op, values: [val] };
        })
      );
      if (filterItems.length > 0) {
        children.push(filterItems.length === 1 ? filterItems[0] : { logicOperator: logicOp, children: filterItems });
      }
    }

    // 7. itemFilterList (필터 모달 - 품목 속성 필터)
    if (size(itemFilterList) > 0) {
      const op = getOperator(itemFilterType);
      const logicOp = getLogic(itemFilterType);
      const filterItems = Object.entries(itemFilterList).flatMap(([key, values]) =>
        (values ?? []).map(val => {
          const filterName = key === 'name' ? 'itemName' : normalizeScopedAttrFilterName(key, "item");
          const hdr = findHeader(filterName, "item");
          return { filterType: "string", headerName: hdr, filterName, operator: op, values: [val] };
        })
      );
      if (filterItems.length > 0) {
        children.push(filterItems.length === 1 ? filterItems[0] : { logicOperator: logicOp, children: filterItems });
      }
    }


    return children.length > 0 ? { logicOperator: 'AND', children } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilterColumnDefs, filterValue, custFilter, itemFilter, custFilterType, itemFilterType,
    customerFilterList, itemFilterList, invClassCdList, excludeEos, filterStartDate, filterEndDate, advancedFilters, dpABCConfig, selectTDCombo]);

  const getAdvancedFilterLeaves = useCallback((node) => {
    if (!node) {
      return [];
    }
    if (node.children?.length) {
      return node.children.flatMap(getAdvancedFilterLeaves);
    }
    return [node];
  }, []);

   // To sort column definition grouping indexes in order
  const getGroupIndex = (orderByItemParam, colNm, i = 0) => {
    const attrCnt = 10;
    const hrchyCnt = 5;
    const codeCnt = 2;

    // Reserve one stable slot between HRCHY and ATTR for INV_CLASS (ITEM_ATTR - 1).
    const invClassGap = 1;
    const blockSize = hrchyCnt + invClassGap + attrCnt + codeCnt;

    const firstBase = 0;
    const secondBase = blockSize;

    const firstIndex = {
      HRCHY: firstBase + i,
      ATTR: firstBase + hrchyCnt + invClassGap + i,
      CODE: firstBase + hrchyCnt + invClassGap + attrCnt,
      LEVEL: firstBase + hrchyCnt + invClassGap + attrCnt + 1,
    };

    const secondIndex = {
      HRCHY: secondBase + i,
      ATTR: secondBase + hrchyCnt + invClassGap + i,
      CODE: secondBase + hrchyCnt + invClassGap + attrCnt,
      LEVEL: secondBase + hrchyCnt + invClassGap + attrCnt + 1,
    };

    const itemIndex = orderByItemParam ? firstIndex : secondIndex;
    const custIndex = orderByItemParam ? secondIndex : firstIndex;

    const columnName = {
      CUSTOMER_HRCHY : custIndex.HRCHY,
      CUSTOMER_ATTR : custIndex.ATTR,
      CUSTOMER_CODE : custIndex.CODE,
      CUSTOMER_LEVEL : custIndex.LEVEL,
      ITEM_HRCHY : itemIndex.HRCHY,
      ITEM_ATTR: itemIndex.ATTR,
      ITEM_CODE: itemIndex.CODE,
      ITEM_LEVEL : itemIndex.LEVEL,
    }

    return columnName[colNm];
  };

  const getHrcyColumnOnParams = (grpLevel, selectHG) => {
    let hrchyColumn = 1;
    if (grpLevel.length > 0) {
      hrchyColumn = grpLevel.filter((el) => el.grpCd === selectHG)[0]?.hrchyNum ?? 1;
    }
    return hrchyColumn;
  }

  const getPageOnParams = () => {
    let paramsPage = 0;
    if (currentPage) {
      if (reload) {
        return paramsPage;
      }
      if ((size(customerFilterList) > 0 || size(itemFilterList) > 0) && !reload) {
        paramsPage = currentPage - 1;
      } else {
        paramsPage = reload ? 0 : currentPage - 1;
        if (isEqual(currentPage, reduxViewState?.currentPage)) {
          paramsPage = currentPage === 1 ? 0 : currentPage - 1;
        }
      }
    }

    return paramsPage;
  }

  const setLoadingCondition = () => {
    if (!isLoading) {
      if (selectCL !== "" && selectIL !== "" && size(selectVersion) > 0
        && !isNull(dimensionAttrData) && !isNull(orderByItem)
        && !isNull(currentPage) && !isNull(pageSize) && !isNull(attrConfig)) {
        setIsLoading(true);
      }
    }
  };

  const getLevelCdOnParams = (isCust) => {
    const bottomCode = isCust ? customer.cd : item.cd;
    let levelCd = bottomCode;
    const filter = isCust ? filterValue.customerHrchys : filterValue.category;
    const selectLvCd = isCust ? selectCL : selectIL;
    const categoryfilterList = filter?.map(data => data?.hrchyCd);
    const bottomFilterList = isCust ? customerFilterList : itemFilterList;

    if (size(bottomFilterList) > 0) {
      levelCd = bottomCode;
    } else {
      levelCd = size(categoryfilterList) > 0 ? filter[0]?.hrchyLv : selectLvCd ?? bottomCode;
    }
    if (isLevel.current) {
      levelCd = selectLvCd;
    }

    return levelCd;
  }

  const getSaveLevelCd = (isCust) => {
    const resultLevelCd = isCust ? resultCustLevelCd.current : resultItemLevelCd.current;
    return resultLevelCd ?? getLevelCdOnParams(isCust);
  }

  const getGridLevelCd = (isCust) => (isCust ? advancedFilterDrillLevel.current?.custLevelCd : advancedFilterDrillLevel.current?.itemLevelCd)
    ?? (isCust ? advancedFilters?.custLevelCd : advancedFilters?.itemLevelCd) ?? getSaveLevelCd(isCust);

  const findHrchyConfigRow = (isCust, levelCd) => {
    const bottomCd = isCust ? customer.cd : item.cd;
    // 최하위(Customer/Item) 는 계층 설정 행이 없다
    if (!levelCd || isEqual(levelCd, bottomCd)) {
      return null;
    }
    const rows = (grpLevel ?? []).filter(lv => isEqual(lv.cd, levelCd) && (isCust ? !lv.itemHrchyFlg : lv.itemHrchyFlg));
    if (size(rows) < 2) {
      return rows[0] ?? null;
    }
    const afLevelCd = isCust ? advancedFilters?.custLevelCd : advancedFilters?.itemLevelCd;
    if (afLevelCd) {
      const afHrchyNum = (isCust ? advancedFilters?.custHrchyNum : advancedFilters?.itemHrchyNum) ?? codeUtil.hrchyNum.HRCHY;
      return rows.find(lv => isEqual(lv.hrchyNum, afHrchyNum)) ?? rows[0];
    }
    return rows.find(lv => isEqual(lv.grpCd, isCust ? selectCHG : selectIHG)) ?? rows[0];
  };

  const getGridGrpCd = (isCust, levelCd) => findHrchyConfigRow(isCust, levelCd)?.grpCd ?? (isCust ? selectCHG : selectIHG);

  /*
   * 최하위(Customer/Item) 레벨은 계층 설정 행이 없어 hrchy num 을 레벨로 못 찾는다.
   *   고급 필터 상태라면 숨겨진 selectCHG/selectIHG(대개 그룹 1) 로 떨어지면 안 되므로
   *   drill 이 기록한 축별 hrchy num → 고급 필터 hrchy num 순으로 먼저 본다.
   */
  const getGridHrchyNum = (isCust) => findHrchyConfigRow(isCust, getGridLevelCd(isCust))?.hrchyNum
    ?? (isCust ? advancedFilterDrillLevel.current?.custHrchyNum : advancedFilterDrillLevel.current?.itemHrchyNum)
    ?? getActiveAdvancedFilterHrchyNum(isCust)
    ?? getHrcyColumnOnParams(isCust ? custGrpLevel : itemGrpLevel, isCust ? selectCHG : selectIHG);

  /*
   * 고급 필터의 계층 그룹 번호는 고급 필터가 실제로 조건/레벨을 가질 때만 쓴다.
   *   기간만 두고 Confirm 하면 조건 없는 고급 필터가 그때의 그룹(예: group 2)만 들고 남는데, 그 상태에선 상단 필터가 그대로 살아 있다.
   *   상단에서 group 1 로 바꿔 계층 필터를 걸어도 이 번호가 custHrchyColumn 을 덮어써 group 1 코드를 group 2 에서 찾아 0 건이 됐다(TC-57).
   */
  const getActiveAdvancedFilterHrchyNum = (isCust) => {
    if (!hasAdvancedFilterChildren(advancedFilters)) {
      return undefined;
    }
    return (isCust ? advancedFilters?.custHrchyNum : advancedFilters?.itemHrchyNum) ?? undefined;
  };

  const statusValueCellRenderer = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  };

  const autoSizeAll = (grid, skipHeader) => {
    const allColumnIds = [CUST_LEVEL_ID, PROD_LEVEL_ID];
    if (grid) {
      grid.autoSizeColumns(allColumnIds, skipHeader);
    }
  };

  const onBtShowLoading = useCallback(() => {
    gridRef.current?.api?.setGridOption('loading', true);
  }, [])

  const onBtHide = useCallback(() => {
    gridRef.current?.api?.setGridOption('loading', false);
  }, [])

  const clearLayout = () => {
    setLayoutId(null);
    setAppliedLayout(null);
    setLayoutModalData({ reloadFalse: false });
    if (reduxViewState) {
      reduxViewState.layoutId = null;
      reduxViewState.layoutModalData = { reloadFalse: false };
      reduxViewState.appliedLayout = null;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  };

  const handleDimenClose = () => {
    setDimenModalOpen(false);
    setDimenSelectLv({});
  };

  /*
   * 최하위(Item/Customer) 레벨의 상위 계층 컬럼 — 지금 조회 중인 그룹(요청에 실은 hrchy column)에 저장된 레벨만 쓴다.
   *   그룹 1 로 보는데 그룹 2 에 저장한 상위 계층(예: Manager)이 뜨면 안 된다(TC-56). 그룹 2 로 바꾸면 그 컬럼이 뜬다.
   *   서버(P13nService.getBottomLevels)와 같은 규칙이어야 displayParXxxHrchy 인덱스가 맞는다:
   *   조회 그룹 번호는 양쪽 모두 요청의 custHrchyColumn/itemHrchyColumn(= getGridHrchyNum) 이고,
   *   (grpCd, cd) 로 계층 설정 행을 찾고(cd 는 그룹끼리 겹칠 수 있다) seqNum 순으로 정렬한다.
   */
  const getBottomHrchyLevels = (isItem, grpObjs, displayHrchyNum) => {
    const levelByKey = {};
    Object.entries(grpObjs ?? {}).forEach(([grpCd, grpObj]) => {
      (grpObj?.configObj ?? []).forEach(config => {
        const configRow = (grpLevel ?? []).find(lv => isEqual(lv.grpCd, grpCd) && isEqual(lv.cd, config?.cd) && Boolean(lv.itemHrchyFlg) === isItem);
        if (configRow) {
          const key = `${configRow.hrchyNum}_${configRow.seqNum}`;
          levelByKey[key] = levelByKey[key] ?? configRow;
        }
      });
    });
    return sortBy(Object.values(levelByKey).filter(level => isEqual(level.hrchyNum, displayHrchyNum)), "seqNum");
  };

  const setDimensionColDefs = (oriColDefs, result) => {
    const custColDefs = [];
    const itemColDefs = [];

    const getHrchyColDefs = (isItem) => {
      const col = isItem ? item : customer;
      const selectLv = isItem ? result.itemLevelCd : result.custLevelCd;
      const cols = isItem ? [ITEM, "itemHrchy"] : ["customer", "custHrchy"];
      // const colId = isItem ? PROD_LEVEL : CUST_LEVEL;
      const selectCol = isEqual(selectLv, col.cd) ? cols[0] : cols[1];
      // AF 가 상단 필터와 다른 그룹의 레벨을 잡아도 그 레벨이 속한 그룹/seqNum 을 쓴다 (findHrchyConfigRow 주석 참고)
      const selectLvConfig = findHrchyConfigRow(!isItem, selectLv);
      const selectGrpCd = selectLvConfig?.grpCd ?? (isItem ? selectIHG : selectCHG);
      const hrchyList = [];

      if (!selectCol.includes("Hrchy")) {
        // 최하위 레벨 — 조회 그룹에 저장된 레벨만 (getBottomHrchyLevels 주석 참고)
        hrchyList.push(...getBottomHrchyLevels(isItem, dimensionAttrData?.[selectCol], getGridHrchyNum(!isItem)));
      } else if (dimensionAttrData?.[selectCol]?.[selectGrpCd]?.configObj) {
        // 계층 레벨 행은 그 레벨이 속한 그룹 트리의 노드라 같은 그룹의 상위 레벨만 붙는다
        const { configObj } = dimensionAttrData[selectCol][selectGrpCd];
        const levelSeqNum = selectLvConfig?.seqNum;
        for (const config of configObj) {
          if (config.seqNum < levelSeqNum) {
            hrchyList.push(config);
          }
        }
      }

      if (size(hrchyList) > 0) {
        const sortHrchyList = sortBy(hrchyList, "seqNum");
        const hrchyNm = cols[1].replace(/^[a-z]/, char => char.toUpperCase());
        for (let i = 0; i < sortHrchyList.length; i ++) {
          const hrchyArry = [
            {
              headerName: sortHrchyList[i].name,
              headerTooltip: sortHrchyList[i].grpName ? `${sortHrchyList[i].grpName} - ${sortHrchyList[i].name}` : sortHrchyList[i].name,
              colId: `display_${hrchyNm}_${String(i + 1).padStart(2, "0")}`,
              showRowGroup: `displayPar${hrchyNm}${String(i + 1).padStart(2, "0")}`,
              cellRenderer: "agGroupCellRenderer",
              chartDataType: "category",
              pinned: "left", lockPinned: true,
              cellClass: "stringType",
              cellRendererParams: {
                suppressCount: true,
              },
              width: 150,
            },
            {
              headerName: sortHrchyList[i].name,
              field: `displayPar${hrchyNm}${String(i + 1).padStart(2, "0")}`,
              editable: false,
              pinned: "left", lockPinned: true,
              hide: true,
              rowGroupIndex: isItem ? getGroupIndex(result.orderByItem, "ITEM_HRCHY", i) : getGroupIndex(result.orderByItem, "CUSTOMER_HRCHY", i),
              cellClass: "stringType",
              rowGroup: true,
            },
          ];
          if (isItem) {
            itemColDefs.push(...hrchyArry);
          } else {
            custColDefs.push(...hrchyArry);
          }
        }
      }
    };

    getHrchyColDefs(true);
    getHrchyColDefs(false);

    if (dimensionAttrData?.item?.[selectIHG]?.hasAbcXyz && isEqual(result.itemLevelCd, item.cd)) {
      const sort = isEqual(sortParams?.orderByColumn, "inv_class_id") ? sortParams?.sortType : null;
      const itemAbcXyz = [
        {
          headerName: INV_CLASS_CD,
          colId: "inv_class_id",
          showRowGroup: INV_CLASS_CD,
          chartDataType: "category",
          pinned: "left", lockPinned: true,
          cellRenderer: "agGroupCellRenderer",
          cellClass: "stringType",
          cellRendererParams: {
            suppressCount: true,
            // 등급 배지(칩)는 원래 틴트와 동일 조건 — 첫 measure 행(measureSeq===1)에만. 그 외는 평문 값 유지.
            innerRenderer: (params) => {
              const { value, data } = params;
              const hasDisplayValue = value !== null && value !== undefined && `${value}`.trim() !== "";
              if (hasDisplayValue && data && data.measureSeq === 1) {
                return <AbcXyzChipInline value={value} />;
              }
              return value ?? "";
            },
          },
          width: 130,
          sortable: true,
          sort
        },
        {
          headerName: INV_CLASS_CD,
          field: INV_CLASS_CD,
          editable: false,
          pinned: "left", lockPinned: true,
          rowGroupIndex: getGroupIndex(result.orderByItem, "ITEM_ATTR") - 1,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        }
      ];
      itemColDefs.push(...itemAbcXyz);
    }

    if (dimensionAttrData?.customer?.[selectCHG]?.attr && isEqual(result.custLevelCd, customer.cd)) {
      const attrDatas = cloneDeep(dimensionAttrData.customer[selectCHG].attr);
      for (let i = 0; i < attrDatas.length; i ++) {
        const attrData = attrDatas[i];
        const attrId = `cust_${attrData}`;
        const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
        const attrNm = attrConfig?.customer ? attrConfig.customer[attrData] ?? attrData : attrData;
        const custAttr = "custAttr";
        const fieldNm = custAttr + i;

        const custAttrArry = [
          {
            headerName: attrNm,
            colId: attrId,
            showRowGroup: fieldNm,
            chartDataType: "category",
            pinned: "left", lockPinned: true,
            cellRenderer: "agGroupCellRenderer",
            cellClass: "stringType",
            cellRendererParams: {
              suppressCount: true,
            },
            width: 120,
            sortable: true,
            sort
          },
          {
            headerName: attrNm,
            field: fieldNm,
            editable: false,
            pinned: "left", lockPinned: true,
            rowGroupIndex: getGroupIndex(result.orderByItem, "CUSTOMER_ATTR", i),
            hide: true,
            cellClass: "stringType",
            rowGroup: true,
            sort
          }
        ];
        custColDefs.push(...custAttrArry);
      }
    }

    if (dimensionAttrData?.item?.[selectIHG]?.attr && isEqual(result.itemLevelCd, item.cd)) {
      const attrDatas = dimensionAttrData.item[selectIHG].attr;
      for (let i = 0; i < attrDatas.length; i ++) {
        const attrData = attrDatas[i];
        const attrId = `prod_${attrData}`;
        const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
        const attrNm = attrConfig?.item ? attrConfig.item[attrData] ?? attrData : attrData;
        const itemAttr = "itemAttr";
        const fieldNm = itemAttr + i;

        const itemAttrArry = [
          {
            headerName: attrNm,
            colId: attrId,
            showRowGroup: fieldNm,
            chartDataType: "category",
            pinned: "left", lockPinned: true,
            cellRenderer: "agGroupCellRenderer",
            cellClass: "stringType",
            cellRendererParams: {
              suppressCount: true,
            },
            width: 120,
            sortable: true,
            sort
          },
          {
            headerName: attrNm,
            field: fieldNm,
            editable: false,
            pinned: "left", lockPinned: true,
            rowGroupIndex: getGroupIndex(result.orderByItem, "ITEM_ATTR", i),
            hide: true,
            cellClass: "stringType",
            rowGroup: true,
            sort
          }
        ];
        itemColDefs.push(...itemAttrArry);
      }
    }

    if (dimensionAttrData?.item?.[selectIHG]?.hasCode && isEqual(result.itemLevelCd, item.cd)) {
      const sort = isEqual(sortParams?.orderByColumn, "prod_CdId") ? sortParams?.sortType : null;
      const itemCode = [
        {
          headerName: ITEM_CD,
          colId: ITEM_CD_ID,
          showRowGroup: ITEM_CD,
          chartDataType: "category",
          pinned: "left", lockPinned: true,
          cellRenderer: "agGroupCellRenderer",
          cellClass: "stringType",
          cellRendererParams: {
            suppressCount: true,
          },
          width: 150,
          sortable: true,
          sort
        },
        {
          headerName: ITEM_CD,
          field: ITEM_CD,
          editable: false,
          pinned: "left", lockPinned: true,
          rowGroupIndex: getGroupIndex(result.orderByItem, "ITEM_CODE"),
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        },
      ];
      itemColDefs.push(...itemCode);
    }

    if (dimensionAttrData?.customer?.[selectCHG]?.hasCode && isEqual(result.custLevelCd, customer.cd)) {
      const sort = isEqual(sortParams?.orderByColumn, "cust_CdId") ? sortParams?.sortType : null;
      const custCode = [
        {
          headerName: CUST_CD,
          colId: CUST_CD_ID,
          showRowGroup: CUST_CD,
          cellRenderer: "agGroupCellRenderer",
          chartDataType: "category",
          pinned: "left", lockPinned: true,
          cellClass: "stringType",
          cellRendererParams: {
            suppressCount: true,
          },
          width: 140,
          sortable: true,
          sort
        },
        {
          headerName: CUST_CD,
          field: CUST_CD,
          editable: false,
          pinned: "left", lockPinned: true,
          rowGroupIndex: getGroupIndex(result.orderByItem, "CUSTOMER_CODE"),
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        },
      ];
      custColDefs.push(...custCode);
    }
    const custIdx = oriColDefs.findIndex(col => isEqual(col.field, CUST_LEVEL_ID));
    oriColDefs.splice(custIdx, 0, ...custColDefs);
    const itemIdx = oriColDefs.findIndex(col => isEqual(col.field, PROD_LEVEL_ID));
    oriColDefs.splice(itemIdx, 0, ...itemColDefs);

    return oriColDefs;
  };

  const getLvHeaderNm = (isCust, result) => {
    if (!result) {
      return isCust ? t("customer") : t(ITEM);
    }
    const selectLv = isCust ? result.custLevelCd : result?.itemLevelCd;
    const selectLevel = isCust ? custLevel : itemLevel;
    const filterLevel = selectLevel?.filter(lv => isEqual(lv.cd, selectLv));

    if ([item.cd, customer.cd].includes(selectLv)){
      return isCust ? t("customer") : t(ITEM);
    } else {
      /*
       * custLevel/itemLevel 은 상단 Hrchy Filter 가 고른 그룹으로 좁혀진 목록이라,
       * AF 가 다른 그룹의 레벨을 잡으면 여기서 못 찾아 헤더가 빈칸이 됐다.
       * → 계층 설정 전체(grpLevel) 에서 그 레벨 행을 찾아 이름을 쓰고, 그래도 없으면 최하위 라벨로 떨어진다.
       */
      return findHrchyConfigRow(isCust, selectLv)?.name ?? filterLevel[0]?.name ?? (isCust ? t("customer") : t(ITEM));
    }
  }

  const measureTotalCols = [
    {
      headerName: "measure",
      field: "measure",
      pinned: "left", lockPinned: true,
      valueFormatter: statusValueCellRenderer,
      // Advanced Filter 가 있을 때 BF 행에만 "?" 안내를 붙인다 (BfInfoCellRenderer)
      cellRenderer: BfInfoCellRenderer,
      cellRendererParams: { t },
      cellStyle({ data }) {
        return data?.measureEditable ? { backgroundColor: editableColor } : null;
      },
      editable({ data }) {
        return data?.measureEditable;
      },
      width: 180,
      suppressAutoSize: true,
      chartDataType: "category",
      menuTabs: ["generalMenuTab"],
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isClicked: setModalOpen,
      },
      lockPosition: 'right',
      cellClass: 'locked-col'
    },
    {
      headerName: "measureSeq",
      field: "measureSeq",
      colId: "measureSeq",
      editable: false,
      sort: "asc",
      sortIndex: 1,
      hide: true,
      width: 20,
      chartDataType: "excluded",
      suppressColumnsToolPanel: true
    },
    {
      headerName: "total",
      field: "total",
      editable: false,
      cellStyle: { color: blue[800] },
      pinned: "left", lockPinned: true,
      width: 110,
      type: ["decimalPointColumn", "rightAligned"],
      chartDataType: "series",
      valueGetter: WorkbenchUtil.totalValueGetter,
      cellRenderer: WorkbenchUtil.numberValueFormatBlank,
      lockPosition: 'right',
    },
    {
      headerName: "EditableMeasure",
      colId: "editable",
      field: "editable",
      hide: true,
      chartDataType: "excluded",
      suppressColumnsToolPanel: true
    },
    {
      headerName: "editableCustomerHrchy",
      colId: "editable_customerHrchy",
      field: "editable",
      hide: true,
      chartDataType: "excluded",
      suppressColumnsToolPanel: true
    },
    {
      headerName: "ResultRow",
      colId: "resultRow",
      field: "resultRow",
      type: ["defaultValueFormatter"],
      hide: true,
      chartDataType: "excluded",
      suppressColumnsToolPanel: true
    },
  ];

  const colDefs = result => [
    {
      headerName: getLvHeaderNm(true, result),
      field: CUST_LEVEL_ID,
      editable: false,
      pinned: "left", lockPinned: true,
      rowGroupIndex: getGroupIndex(result.orderByItem, "CUSTOMER_LEVEL"),
      hide: true,
      cellClass: "stringType",
      rowGroup: true,
      sortable: true,
      sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,
    },
    {
      headerName: getLvHeaderNm(true, result),
      colId: CUST_LEVEL,
      showRowGroup: CUST_LEVEL_ID,
      chartDataType: "category",
      type: ["nonEditableColumn"],
      cellRenderer: "agGroupCellRenderer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          const selectLvCd = result?.custLevelCd ?? getLevelCdOnParams(true);
          setDimenModalOpen(true);
          setDimenSelectLv({
            isCust: true,
            selectLvCd,
            // 편집하는 그룹 탭 = 지금 그려진 레벨이 속한 그룹 (AF 가 다른 그룹을 잡았으면 selectCHG 와 다르다)
            selectGrpCd: getGridGrpCd(true, selectLvCd)
          })
        }
      },
      cellRendererParams: {
        suppressCount: true,
      },
      width: 150,
    },
    {
      headerName: getLvHeaderNm(false, result),
      field: PROD_LEVEL_ID,
      editable: false,
      pinned: "left", lockPinned: true,
      hide: true,
      rowGroupIndex: getGroupIndex(result.orderByItem, "ITEM_LEVEL"),
      cellClass: "stringType",
      rowGroup: true,
      sortable: true,
      sort: isEqual(sortParams?.orderByColumn, PROD_LEVEL_ID) ? sortParams?.sortType : null,
    },
    {
      headerName: getLvHeaderNm(false, result),
      colId: PROD_LEVEL,
      showRowGroup: PROD_LEVEL_ID,
      chartDataType: "category",
      cellRenderer: "agGroupCellRenderer",
      pinned: "left", lockPinned: true,
      cellClass: "stringType",
      sort: isEqual(sortParams?.orderByColumn, PROD_LEVEL_ID) ? sortParams?.sortType : null,
      // suppressHeaderMenuButton: true,
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          const selectLvCd = result?.itemLevelCd ?? getLevelCdOnParams(false);
          setDimenModalOpen(true);
          setDimenSelectLv({
            isCust: false,
            selectLvCd,
            selectGrpCd: getGridGrpCd(false, selectLvCd)
          })
        }
      },
      cellRendererParams: {
        suppressCount: true,
      },
      width: 150,
    }
  ];

  const setGridHeaders = (headers, result, dtf, startDate) => {
    const timeDimension = result.timeDimension;
    const formatHeaderList = [];
    const editableHeader = [];

    const basicColumn = colDefs(result);
    const custBasicColums = basicColumn.slice(0, 2);
    const itemBasicColums = basicColumn.slice(2, 4);

    let range = 12;
    let editableStartDate = startDate;
    let headerStartDay = startDay.current;
    if (selectVersion && selectVersion.statusCd === dpVersion.SCENARIO) {
      if (selectVersion.planConfigObj) {
        editableStartDate = new ZDate(selectVersion.planConfigObj.PLAN_START_DATE);
        range = selectVersion.planConfigObj.PLANNING_RANGE;
        headerStartDay = selectVersion.planConfigObj.START_DAY;
      }
      if (!codeUtil.isWeekBucket(timeDimension)) {
        editableStartDate.setDate(1);
      }
    }
    editableStartDate.setFullYear(editableStartDate.getFullYear());
    editableStartDate.setMonth(editableStartDate.getMonth());
    editableStartDate.setHours(0);
    editableStartDate.setMinutes(0);
    editableStartDate.setSeconds(0);
    const editableEndDate = dateUtils.calculateRangeDate(editableStartDate, timeDimension, headerStartDay, range, true);
    editableEndDate.setHours(0);
    editableEndDate.setMinutes(0);
    editableEndDate.setSeconds(0);
    if (!codeUtil.isWeekBucket(timeDimension)) {
      editableStartDate.setDate(1);
      editableEndDate.setDate(1);
    }
    if (dtf && timeDimension) {
      editableStartDate = WorkbenchEditable.getEditDateByDtf(dtf, editableStartDate, timeDimension) ?? editableStartDate;
    }

    for (const index in headers) {
      if (Object.hasOwn(headers, index)) {
        const header = headers[index];
        const temp = new ZDate(header);
        const date = new ZDate(temp.getFullYear(), temp.getMonth(), temp.getDate(), 0, 0, 0);
        let format = dateUtils.headerDateFormatter(date, timeDimension);
        if (codeUtil.isWeekBucket(timeDimension)) {
          format = weekFormatHeader.current[header];
        }
        if (dateUtils.isBetween(date, editableStartDate, editableEndDate)) {
          if (!editableHeaderIdx.current || editableHeaderIdx.current > index) {
            editableHeaderIdx.current = index;
          }
          editableHeader.push(true);
        } else {
          editableHeader.push(false);
        }
        formatHeaderList.push(format);
      }
    }

    const editableArry = [];

    const columnsDefs = result?.orderByItem ? [...itemBasicColums, ...custBasicColums] : basicColumn;
    columnsDefs.push(...measureTotalCols);

    for (const index in formatHeaderList) {
      if (Object.hasOwn(formatHeaderList, index)) {
        const header = formatHeaderList[index];
        const todayEditable = editableHeader[index];
        if (todayEditable) {
          editableArry.push(header);
        }
        const editableParams = {
          selectVersion,
          editableMeasure: editableMeasure.current,
          custGrpLevel, itemGrpLevel,
          selectTD: timeDimension,
          selectCHG, selectIHG,
          selectCL: result.custLevelCd,
          selectIL: result.itemLevelCd
        };
        columnsDefs.push({
          headerName: header,
          field: header,
          width: 110,
          singleClickEdit: false,
          type: ["decimalPointColumn", "rightAligned"],
          chartDataType: "series",
          cellRenderer: WorkbenchUtil.numberValueFormatBlank,
          cellDataType: false,
          cellEditor: NumericEditor,
          cellStyle: data => WorkbenchEditable.dataCellStyle(data, header, reverseHeader.current, editableStartDate, todayEditable, editableParams, timeDimension, index),
          editable({ data }) {
            return WorkbenchEditable.checkEditable(data, editableParams, editableStartDate, todayEditable, codeUtil.isWeekBucket(timeDimension) ? reverseHeader.current[header] : header);
          },
          cellClassRules: {
            editableBackgroud : ({ data }) => WorkbenchEditable.checkEditable(data, editableParams, editableStartDate, todayEditable, codeUtil.isWeekBucket(timeDimension) ? reverseHeader.current[header] : header),
            alertBackground: data => WorkbenchEditable.getAlertBackground(data, header),
            noteAlert: data => WorkbenchEditable.setNoteColor(data)
          },
        });
      }
    }
    const dimenColDefs = size(dimensionAttrData) > 0 ? setDimensionColDefs(columnsDefs, result) : columnsDefs;
    const targetMenuDefs = dimenColDefs.map(def => ({ ...def, suppressHeaderMenuButton: true, lockPinned: true, lockPosition: "left" }));

    return { headers: formatHeaderList, colDefs: targetMenuDefs, editableHeader: uniq(editableArry) };
  };

  const handleLoadData = response => {
    setReload(false);
    responseRef.current = response;
    const headers = response.data.resultData.headers;
    workbenchThreshold.current = response?.data?.resultData?.workbenchThreshold ?? {};
    const result = response.data.resultData;
    weekFormatHeader.current = result?.formatHeaders;
    reverseHeader.current = result?.reverseFormatHeaders;
    const pageResult = response.data;
    const dtf = result.dtf ? result.dtf : null;
    const startDate = new ZDate(result.startDate);

    statusReady.current = result.statusReady;
    accessControl.current = result.accessControl;
    approvalAccessControl.current = result.approvalAccessControl;

    const translation = {};

    resultCustLevelCd.current = result.custLevelCd ?? customer.cd;
    resultItemLevelCd.current = result.itemLevelCd ?? item.cd;

    // isLevel && disaggUnitFlg
    const isBottom = isEqual(resultCustLevelCd.current, customer.cd) && isEqual(resultItemLevelCd.current, item.cd);

    editableMeasure.current = result.basicMeasureData
        .filter(el => el.measureEditable && (isBottom || el.disaggUnitFlg))
        .map(el => el.measureCd);

    //translation key for edit excel
    for (const measure of result?.basicMeasureData) {
      translation[measure.measureCd] = t(measure.name);
    }
    setTranslationKey(translation)

    const postnDto = result.postnDto;
    if (size(postnDto) > 1) {
      myPostn.current = postnDto?.map(dto => {
        return { ...dto };
      });
    } else {
      myPostn.current = { ...postnDto[0] };
    }

    /*
     * ⚠ truthy 검사(`if (pageResult.totalPages)`)면 안 된다.
     *   결과가 0건이면 서버가 totalPages/totalItems 를 0 으로 주는데 0 은 falsy 라서 **직전 조회의 페이지 수가 그대로 남는다**
     *   → 그리드는 0건/16건인데 하단 페이저만 4페이지로 보이는 불일치.
     */
    const responseTotalPages = pageResult.totalPages ?? 0;
    const responseTotalRows = pageResult.totalItems ?? 0;
    setTotalPages(responseTotalPages);
    setTotalRows(responseTotalRows);

    if (drill.current) {
      if (!result.custLevelCd || isEqual(result.custLevelCd, customer.cd)) {
        setCustomerFilterList(result?.customerFilterList ?? {});
        setSelectCL(customer.cd);
        setCustomValue([]);
      } else {
        setCustomerFilterList({});
        if (!isEqual(result.custLevelCd, selectCL)) {
          setCustFilter(result.custFilter ?? []);
        }
        setSelectCL(result.custLevelCd);
      }
      if (!result.itemLevelCd || isEqual(result.itemLevelCd, item.cd) ) {
        setItemFilterList(result?.itemFilterList ?? {});
        setSelectIL(item.cd);
        setItemCustomValue([]);
      } else {
        setItemFilterList({});
        if (!isEqual(result.itemLevelCd, selectIL)) {
          setItemFilter(result.itemFilter ?? []);
        }
        setSelectIL(result.itemLevelCd);
      }
      // 고급 필터 상태의 drill 이면 이후 조회(페이지 이동·재조회)도 drill 한 레벨로 나가야 한다.
      if (hasAdvancedFilterChildren(advancedFilters)) {
        // 요청에 쓴 계층 그룹을 같이 남긴다 — 최하위까지 내려가면 레벨로는 그룹을 되찾을 수 없다.
        advancedFilterDrillLevel.current = {
          custLevelCd: result.custLevelCd ?? customer.cd,
          itemLevelCd: result.itemLevelCd ?? item.cd,
          custHrchyNum: getGridHrchyNum(true),
          itemHrchyNum: getGridHrchyNum(false),
        };
      }
    }
    /*
     * 고급 필터가 조건/레벨을 가진 상태면 상단 계층 필터에 서버의 계층 목록을 심지 않는다.
     *   레벨만 고르고 계층 조건이 없으면 서버는 그 레벨의 모든 노드(예: All 레벨 → "All item")를 돌려주는데,
     *   이게 상단 필터에 들어가면 고급 필터 레벨을 Item 으로 되돌려도 상단 필터가 그 레벨을 붙잡고 남는다.
     *   drill 은 상단 필터를 drill 위치로 옮기는 기존 동작을 유지한다.
     */
    const plantHrchyFilter = drill.current || !hasAdvancedFilterChildren(advancedFilters);
    if (result.itemHrchyFilter && plantHrchyFilter) {
      setItemCustomValue(result.itemHrchyFilter);
    }
    if (result.custHrchyFilter && plantHrchyFilter) {
      setCustomValue(result.custHrchyFilter);
    }

    if (result.timeDimension) {
      selectTD.current = result.timeDimension;
      setSelectTDCombo(result.timeDimension);
    }

    setFilterStartDate(startDate);
    setFilterEndDate(new ZDate(result.endDate));

    if (result.planningRange) {
      setPlanningRange(result.planningRange);
    }
    const responsePageSize = pageResult.pageSize ?? pageSize;
    if (responsePageSize !== pageSize) {
      setPageSize(responsePageSize);
    }
    if (result.measureDict) {
      const measures = {};
      if (!result.visibleMeasures) {
        setMeasure(result.measureDict);
      } else {
        for (const active of Object.keys(result.measureDict)) {
          if (result.visibleMeasures.includes(active)) {
            measures[active] = result.measureDict[active];
          }
        }
        setMeasure(measures);
      }
    }
    if (result.startDay) {
      startDay.current = result.startDay;
    }

    dataMap.current = WorkbenchUtil.makeDataMap(result);

    const gridHeaders = setGridHeaders(headers, result, dtf, startDate);
    const gridData = WorkbenchUtil.setGridDatas(result, gridHeaders.headers, userData, isDrillSingleMeasure.current ? drillMeasure.current : null, displayNameRef.current);
    if (result.pivotdata) {
      setPivotData(result.pivotdata);
      setPivotDataCopy(cloneDeep(result.pivotdata));
    }
    setEditableHeaderSet(gridHeaders.editableHeader);
    setFormatGridHeader(gridHeaders.headers);
    setColumnDefs(gridHeaders.colDefs);

    // Set the column definitions for the grid (including the dimension columns)
    gridApi?.setGridOption('columnDefs', GridUtils.getColumnDefs(gridHeaders.colDefs));
    // 언어 변경 시 헤더를 다시 번역할 수 있도록 번역 전 원본을 등록한다.
    GridUtils.registerLocalizedGrid(gridApi, gridHeaders.colDefs);

    setChartData(WorkbenchUtil.setChartDatas(result.pivotdata, gridHeaders.headers, enabledChartMeasure, editableMeasure, userData?.currencySymbol));
    autoSizeAll(gridApi, true);
    setWorkbench(gridData);
    if (isEmpty(gridData)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
    }
    const versionId = selectVersion?.id;
    const reduxObj = {
      /*
       * ⚠ totalRows/pageSize 는 위에서 setState 한 **응답값**을 써야 한다.
       *   React state 변수(totalRows, pageSize)는 이 렌더의 값이라 아직 직전 조회의 수치다
       *   → redux 에 옛 페이지 수가 박혀 다음 진입 때 페이저가 어긋난 상태로 복원된다.
       */
      page: currentPage,
      pageSize: responsePageSize,
      currentPage,
      totalRows: responseTotalRows,
      selectVersion,
      versionId,
      sortType,
      isDrillSingleMeasure: isDrillSingleMeasure.current,
      drillMeasure: drillMeasure.current,
      custLevelCd: result.custLevelCd,
      itemLevelCd: result.itemLevelCd,
      timeDimension: result.timeDimension,
      startDay: result.startDay,
      startDate,
      endDate: result.endDate,
      custGrpLevelCd: selectCHG ?? custGrpLevel[0].grpCd,
      itemGrpLevelCd: selectIHG ?? itemGrpLevel[0].grpCd,
      grpLevelList: grpLevel,
      custGrpLevelList: custGrpLevel,
      itemGrpLevelList: itemGrpLevel,
      custFilter: result.custFilter ?? [],
      itemFilter: result.itemFilter ?? [],
      customerFilterList: result.customerFilterList ?? {},
      itemFilterList: result.itemFilterList ?? {},
      itemFilterType,
      custFilterType,
      invClassCdList,
      dimensionAttrData,
      orderByItem: result.orderByItem,
      sortParams,
    };
    const currentReduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...currentReduxViewState, ...reduxObj }));
    onBtHide();
  };

  const retrieveDemandVal = (params, clearFlg = false) => {
    const seq = ++retrieveSeqRef.current;
    // 이 조회보다 나중에 던진 조회가 있으면 이 응답은 버린다(retrieveSeqRef 주석 참고).
    const isStaleResponse = () => seq !== retrieveSeqRef.current;
    retrieveInFlightRef.current = true;
    setSaveTarget([]);
    onBtShowLoading();
    if (params.drill) {
      drill.current = true;
    } else {
      drill.current = false;
    }
    if (params.itemLevelCd === item.cd) {
      setDisabledInvClass(false);
    } else {
      setDisabledInvClass(true);
    }
    if (params.custLevelCd === customer.cd) {
      params.custLevelCd = null;
    }
    if (params.itemLevelCd === item.cd) {
      params.itemLevelCd = null;
    }
    if (clearFlg) {
      params.layoutId = null;
    }
    if (isChannel) {
      params.channel = true;
    }
    demandValService.getAll(params)
      .then(response => {
        if (isStaleResponse()) {
          return;
        }
        if (!isEmpty(response.data)) {
          handleLoadData(response);
        } else {
          setReload(false);
          setColumnDefs(colDefs(null));
          setWorkbench([]);
          onBtHide();
        }
      })
      .catch(response => {
        if (isStaleResponse()) {
          return;
        }
        const message = response.response?.data?.message;
        setSnackInfo({ open: true, severity: message?.type || snackBarMessageColor.networkError, content: t(message?.message) || t("MSG_NetworkError") });
        debug(response);
        setWorkbench([]);
        setReload(false);
        onBtHide();
      })
      .then(() => {
        if (isStaleResponse()) {
          return;
        }
        setTreeload(false);
        setItemTreeLoad(false);
        retrieveInFlightRef.current = false;
        if (queuedReloadRef.current) {
          queuedReloadRef.current = false;
          requestReload();
        }
      });

      const currentReduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
      if (currentReduxViewState) {
        currentReduxViewState.custFilter = custFilter
        currentReduxViewState.itemFilter = itemFilter
        currentReduxViewState.customerFilterList = customerFilterList
        currentReduxViewState.itemFilterList = itemFilterList
        currentReduxViewState.itemFilterType = itemFilterType
        currentReduxViewState.custFilterType = custFilterType
        currentReduxViewState.invClassCdList = invClassCdList
        currentReduxViewState.custGrpLevelCd = selectCHG
        currentReduxViewState.itemGrpLevelCd = selectIHG
      }
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...currentReduxViewState }));
  };

  const handleBulkValueUpdate = () => {
    setBulkValueUpdateModalOpen(true);
  };

  const handleBulkValueClose = () => {
    setBulkValueUpdateModalOpen(false);
  };

  const handleNoteModal = () => {
    setWorkbenchNoteOpen(!workbenchNoteOpen);
    if (!workbenchNoteOpen && noteUpdateFlg) {
      requestReload();
      setNoteUpdateFlg(false);
    }
  };

  const getIconOnMenuItem = (title) => {
    const menuIcons = [`<img src=${AggregationIcon} />`, `<img src=${DescIcon} />`, `<img src=${AscIcon} />`];
    if (isEqual(title, "bulkValueUpdate")) {
      return menuIcons[0];
    } else if (includes(title, "Down")) {
      return menuIcons[1];
    }
    return menuIcons[2];
  }

  const handleLevel = (isUp, isCust) => {
    // 고급 필터 상태는 그리드 레벨이 고급 필터(또는 그 위의 drill)가 정한 값이라 별도로 옮긴다.
    if (hasAdvancedFilterChildren(advancedFilters)) {
      handleAdvancedFilterLevel(isUp, isCust);
      return;
    }
    isLevel.current = true;
    const level = isCust ? sortBy(custLevel, "seqNum") : sortBy(itemLevel, "seqNum");
    const selectLv = isCust ? selectCL : selectIL;
    const myIdx = level.findIndex(lv => isEqual(lv.cd, selectLv));
    const selectGrpCd = isCust ? selectCHG : selectIHG;
    const filterdLevel = level.filter(lv => isEqual(lv.grpCd, selectGrpCd));
    if (size(filterdLevel)) {
      if (myIdx >= 0) {
        const bottomLvCd = isCust ? customer.cd : item.cd;
        const clikedLv = isUp ? filterdLevel?.[myIdx - 1]?.cd ?? filterdLevel[0]
                              : filterdLevel?.[myIdx + 1]?.cd ?? bottomLvCd;
        if (isCust) {
          if (size(filterValue?.customerHrchys) > 0) {
            setTreeload(true);
            setCustomerFilterList({});
            setCustFilter([]);
            setCustomValue([]);
          }
          setSelectCL(clikedLv);
        } else {
          if (size(filterValue?.category) > 0) {
            setItemTreeLoad(true);
            setItemFilterList ({});
            setItemFilter ([]);
            setItemCustomValue([]);
          }
          setSelectIL(clikedLv);
        }
        requestReload();
      }
    }
  }

  /*
   * 고급 필터 상태의 레벨 업/다운. 현재 그리드 레벨(getGridLevelCd)이 속한 계층 그룹 안에서 한 단계 옮긴다.
   *   요청 레벨은 고급 필터 레벨로 덮어써지므로 selectCL/selectIL 대신 advancedFilterDrillLevel 에 기록한다.
   *   drill 로 좁힌 범위(custFilter 등)는 그대로 둔다 — 레벨만 바뀌고 범위 안에서 다시 묶인다.
   */
  const handleAdvancedFilterLevel = (isUp, isCust) => {
    const bottomCd = isCust ? customer.cd : item.cd;
    const currentCd = getGridLevelCd(isCust) ?? bottomCd;
    const hrchyNum = findHrchyConfigRow(isCust, currentCd)?.hrchyNum
      ?? (isCust ? advancedFilters?.custHrchyNum : advancedFilters?.itemHrchyNum)
      ?? getGridHrchyNum(isCust);
    const groupLevels = sortBy((grpLevel ?? [])
      .filter(lv => (isCust ? !lv.itemHrchyFlg : lv.itemHrchyFlg) && isEqual(lv.hrchyNum, hrchyNum)), "seqNum")
      .map(lv => lv.cd);
    if (size(groupLevels) === 0) {
      return;
    }
    const orderedLevels = [...groupLevels, bottomCd];
    const currentIdx = orderedLevels.indexOf(currentCd);
    if (currentIdx < 0) {
      return;
    }
    const nextIdx = isUp ? currentIdx - 1 : currentIdx + 1;
    if (nextIdx < 0 || nextIdx >= orderedLevels.length) {
      return;
    }
    const current = advancedFilterDrillLevel.current
      ?? {
        custLevelCd: getGridLevelCd(true) ?? customer.cd,
        itemLevelCd: getGridLevelCd(false) ?? item.cd,
        custHrchyNum: getGridHrchyNum(true),
        itemHrchyNum: getGridHrchyNum(false),
      };
    advancedFilterDrillLevel.current = isCust
      ? { ...current, custLevelCd: orderedLevels[nextIdx], custHrchyNum: hrchyNum }
      : { ...current, itemLevelCd: orderedLevels[nextIdx], itemHrchyNum: hrchyNum };
    setCellInfo({});
    requestReload();
  };

  const getRequestParams = (orderByItemParam) => {
    setLoadingCondition();
    const startdate = WorkbenchUtil.getSearchFormat(filterStartDate, selectTD.current);
    let enddate = null;
    if (filterEndDate !== null) {
      enddate = WorkbenchUtil.getSearchFormat(filterEndDate, selectTD.current);
    }

    const custFilterList = isLevel.current ? custFilter : filterValue.customerHrchys?.map(data => data?.hrchyCd);
    const categoryFilterList = isLevel.current ? itemFilter : filterValue.category?.map(data => data?.hrchyCd);

    const custLevelCd = getLevelCdOnParams(true);
    const itemLevelCd = getLevelCdOnParams(false);

    const params = {
      versionId: selectVersion?.id ?? reduxViewState?.versionId ?? null,
      custLevelCd,
      itemLevelCd,
      custHrchyColumn: getHrcyColumnOnParams(custGrpLevel, selectCHG),
      itemHrchyColumn: getHrcyColumnOnParams(itemGrpLevel, selectIHG),
      customerHrchyType: false,
      headerType: true,
      custFilter: custLevelCd === null || custLevelCd === customer.cd ? [] : custFilterList ? custFilterList : [],
      itemFilter: itemLevelCd === null || itemLevelCd === item.cd ? [] : categoryFilterList ? categoryFilterList : [],
      timeDimension: selectTD.current,
      startDate: startdate,
      endDate: enddate,
      bottomLevel: selectCL === customer.cd && selectIL === item.cd,
      planningRange: planningRange ?? 12,
      startDay: startDay.current,
      layoutId,
      customerFilterList,
      itemFilterList,
      itemFilterType,
      custFilterType,
      invClassCdList,
      sortType,
      singleMeasure: isDrillSingleMeasure.current,
      rowMeasureCd: drillMeasure.current,
      page: getPageOnParams(),
      orderByItem: orderByItemParam ?? orderByItem,
      excludeEos
    };

    if (!isEmpty(advancedFilters)) {
      const advancedFilter = getAdvancedFilterTree(advancedFilters);
      let advancedFilterCustLevelCd = null;
      let advancedFilterItemLevelCd = null;
      let advancedFilterCustHrchyNum = null;
      let advancedFilterItemHrchyNum = null;

      if (advancedFilters?.itemLevelCd || advancedFilters?.custLevelCd) {
        advancedFilterCustLevelCd = advancedFilters.custLevelCd;
        advancedFilterItemLevelCd = advancedFilters.itemLevelCd;
      }

      // 조건 없는 고급 필터의 그룹 번호로 상단 필터의 그룹을 덮어쓰지 않는다(getActiveAdvancedFilterHrchyNum, TC-57)
      advancedFilterCustHrchyNum = getActiveAdvancedFilterHrchyNum(true) ?? null;
      advancedFilterItemHrchyNum = getActiveAdvancedFilterHrchyNum(false) ?? null;

      if (advancedFilterCustLevelCd) {
        params.custLevelCd = advancedFilterCustLevelCd;
        params.custHrchyColumn = getGridHrchyNum(true);
      }

      if (advancedFilterItemLevelCd) {
        params.itemLevelCd = advancedFilterItemLevelCd;
        params.itemHrchyColumn = getGridHrchyNum(false);
      }

      if (advancedFilterCustHrchyNum) {
        params.custHrchyColumn = advancedFilterCustHrchyNum;
      }

      if (advancedFilterItemHrchyNum) {
        params.itemHrchyColumn = advancedFilterItemHrchyNum;
      }

      // 고급 필터 상태에서 drill / 레벨 이동을 했으면 그 레벨이 현재 그리드 레벨이다.
      const drillLevel = advancedFilterDrillLevel.current;
      if (drillLevel) {
        params.custLevelCd = drillLevel.custLevelCd;
        params.itemLevelCd = drillLevel.itemLevelCd;
        params.custHrchyColumn = getGridHrchyNum(true);
        params.itemHrchyColumn = getGridHrchyNum(false);
      }

      /* 기간은 상단 기간(params.startDate/endDate)이 원본 — 트리 baseDate 는 그에 맞춰 보낸다(syncAdvancedFilterBaseDate). */
      if (hasAdvancedFilterChildren(advancedFilters)) {
        /* Exclude EOS 는 트리가 아니라 boolean 파라미터로 나가고, 노드는 빼서 보낸다(EXCLUDE_EOS 주석). */
        params.excludeEos = getExcludeEosFromFilters(advancedFilter);
        const sentAdvancedFilter = syncAdvancedFilterBaseDate(
          stripExcludeEosNodes(advancedFilter), filterStartDate, filterEndDate, selectTD.current);

        // Advanced Filter가 활성화되면 version 외 일반 필터는 중복 적용되지 않도록 제외
        if (hasAdvancedFilterChildren(sentAdvancedFilter)) {
          params.advancedFilters = sentAdvancedFilter;
        }

        /*
         * 단, drill 이 정한 계층 범위는 일반 필터가 아니라 drill 결과다(handleLoadData 가 응답으로 채운다).
         *   서버가 이 범위를 고급 필터 조건으로 AND 하므로, drill 상태가 있을 때만 그대로 보낸다.
         *   최하위까지 내려간 축은 거래처/품목 코드 목록(customerFilterList/itemFilterList)이 범위다.
         *   계층 코드 범위는 서버에서 "노드 자신 또는 조상" 조건이 되므로 레벨이 바뀌어도(레벨 이동) 그대로 유효하다.
         */
        const drillScope = advancedFilterDrillLevel.current;
        params.custFilter = drillScope ? custFilter : [];
        params.itemFilter = drillScope ? itemFilter : [];
        params.customerFilterList = drillScope ? customerFilterList : {};
        params.itemFilterList = drillScope ? itemFilterList : {};
        params.invClassCdList = [];
      }
    }

    if (size(cellInfo) > 0) {
      const cellInfoParams = ["customerHrchyCd", "itemHrchyCd", CUST_CD, ITEM_CD, "drill", "dateType", "columnHeader",
      "headerType", "singleMeasure", "customerHrchyType", "rowMeasure"];

      for (const cellInfoParam of cellInfoParams) {
        params[cellInfoParam] = cellInfo[cellInfoParam];
        if (isEqual(cellInfoParam, "columnHeader") && !cellInfo.dateType) {
          params[cellInfoParam] = cellInfo.customerHrchyType ? CUST_LEVEL : PROD_LEVEL;
        } else if (isEqual(cellInfoParam, "drill")) {
          params[cellInfoParam] = cellInfo.title;
        } else if (isEqual(cellInfoParam, "rowMeasure")) {
          if (!isEmpty(cellInfo[cellInfoParam]) && cellInfo.singleMeasure) {
            isDrillSingleMeasure.current = true;
            drillMeasure.current = cellInfo[cellInfoParam]?.measureCd;
          }
        }
      }
    }

    if (totalRows && params.page > 0) {
      params.totalRows = totalRows;
    }

    if (pageSize) {
       params.pageSize = reload ? reduxViewState?.pageSize : pageSize;
//      params.pageSize = reload ? pageSizes[0] : pageSize;
    }
    const checkSortParms = sortParams?.sortType ? sortParams : {};
    return { ...params, ...checkSortParms };
  };

  const getCustomMenuItems = (editable, columnHeader, computeField, cellData) => {
    const menuItems = [];

    const BULKUPSERT = "bulkValueUpdate";
    const ITEMLVUP = "itemLevelUp";
    const ITEMLVDOWN = "itemLevelDown";
    const CUSTLVUP = "customerLevelUp";
    const CUSTLVDOWN = "customerLevelDown";
    const CUSTDRUP = "customerDrillUp";
    const CUSTDRDOWN = "customerDrillDown";
    const ITEMDRUP = "itemDrillUp";
    const ITEMDRDOWN = "itemDrillDown";
    const DRILL_DOWN = "DRILL_DOWN";
    const DRILL_UP = "DRILL_UP";

    const menuTitles = [
      BULKUPSERT, CUSTDRDOWN, ITEMDRDOWN, CUSTDRUP, ITEMDRUP,
      CUSTLVDOWN, ITEMLVDOWN, CUSTLVUP, ITEMLVUP,
    ];

    /*
     * 메뉴 활성화 기준 레벨. 고급 필터 상태에서는 화면 본체 레벨(selectCL/selectIL)이 아니라 그리드 레벨이 기준이다
     *   (고급 필터가 레벨을 정하면 본체 레벨은 최하위에 머물러 drill down / 레벨 다운이 막혔다).
     *   최상위 판정은 그 레벨이 속한 계층 그룹의 첫 레벨로 한다.
     */
    const isAdvancedFilterMode = hasAdvancedFilterChildren(advancedFilters);
    const getMenuLevelCd = (isCust) => (isAdvancedFilterMode ? getGridLevelCd(isCust) : (isCust ? selectCL : selectIL));
    const getMenuTopLevelCd = (isCust) => {
      if (!isAdvancedFilterMode) {
        return isCust ? custLevel[0]?.cd : itemLevel[0]?.cd;
      }
      const hrchyNum = findHrchyConfigRow(isCust, getGridLevelCd(isCust))?.hrchyNum ?? getGridHrchyNum(isCust);
      return sortBy((grpLevel ?? [])
        .filter(lv => (isCust ? !lv.itemHrchyFlg : lv.itemHrchyFlg) && isEqual(lv.hrchyNum, hrchyNum)), "seqNum")[0]?.cd;
    };
    const menuCL = getMenuLevelCd(true);
    const menuIL = getMenuLevelCd(false);
    const menuTopCL = getMenuTopLevelCd(true);
    const menuTopIL = getMenuTopLevelCd(false);

    const getItemCustCondition = (isCust) => {
      if (isCust) {
        const isAttr = includes(columnHeader, PROD_ATTR) || columnHeader === ITEM_CD_ID;
        // TODO: change compute field
        return includes(columnHeader, PROD_LEVEL) || columnHeader === "itemHrchy" || isAttr; //(columnHeader === "measure" && computeField)
      }
      const isAttr = includes(columnHeader, CUSTOMER_ATTR) || columnHeader === CUST_CD_ID;
      return includes(columnHeader, CUST_LEVEL) || columnHeader === "custHrchy" || isAttr; // (columnHeader === "measure" && computeField)
    };

    const menuName = {
      bulkValueUpdate : {
        icon: getIconOnMenuItem(BULKUPSERT),
        disabled: !editable,
        action: () => {
          const params = getRequestParams();
          setBulkValueNodeData({
            title: t(BULKUPSERT),
            ...cellData,
            custLevel: getGridLevelCd(true),
            itemLevel: getGridLevelCd(false),
            params,
            pivotData,
            accessControl: accessControl.current
          });
          handleBulkValueUpdate();
        }
      },
      itemLevelUp : {
        icon: getIconOnMenuItem(ITEMLVUP),
        disabled: menuIL === menuTopIL || getItemCustCondition(false),
        action: () => {
          handleLevel(true, false);
        }
      },
      itemLevelDown : {
        icon: getIconOnMenuItem(ITEMLVDOWN),
        disabled: menuIL === item.cd || getItemCustCondition(false),
        action: () => {
          handleLevel(false, false);
        }
      },
      customerLevelUp : {
        icon: getIconOnMenuItem(CUSTLVUP),
        disabled:  menuCL === menuTopCL || getItemCustCondition(true),
        action: () => {
          handleLevel(true, true);
        }
      },
      customerLevelDown : {
      icon: getIconOnMenuItem(CUSTLVDOWN),
        disabled: menuCL === customer.cd || getItemCustCondition(true),
        action: () => {
          handleLevel(false, true);
        }
      },
      customerDrillDown : {
        icon: getIconOnMenuItem(CUSTDRDOWN),
        disabled: size(customerFilterList) > 0 || (menuCL === customer.cd && size(customerFilterList) < 1)
        || getItemCustCondition(true),
        action: () => {
          setCellInfo({
            title: DRILL_DOWN,
            ...cellData,
            formatGridHeader,
            customerHrchyType: true,
          });
          requestReload();
        }
      },
      itemDrillDown : {
        icon: getIconOnMenuItem(ITEMDRDOWN),
        disabled: size(itemFilterList) > 0 || (menuIL === item.cd && size(itemFilterList) < 1)
        || getItemCustCondition(false),
        action: () => {
          setCellInfo({
            title: DRILL_DOWN,
            ...cellData,
            formatGridHeader,
            customerHrchyType: false,
          });
          requestReload();
        }
      },
      customerDrillUp : {
        icon: getIconOnMenuItem(CUSTDRUP),
        disabled: menuCL === menuTopCL || getItemCustCondition(true),
        action: () => {
          setCellInfo({
            title: DRILL_UP,
            ...cellData,
            formatGridHeader,
            customerHrchyType: true,
          });
          requestReload();
        }
      },
      itemDrillUp : {
        icon: getIconOnMenuItem(ITEMDRUP),
        disabled:  menuIL === menuTopIL || getItemCustCondition(false),
        action: () => {
          setCellInfo({
            title: DRILL_UP,
            ...cellData,
            formatGridHeader,
            customerHrchyType: false,
          });
          requestReload();
        }
      },
    };

    for (const menuTitle of menuTitles) {
      menuName[menuTitle].name = t(menuTitle);
      menuItems.push(menuName[menuTitle]);
    }
    return menuItems;
  }

  /*
   * 우클릭 메뉴는 매 렌더의 최신 상태(고급 필터 레벨·drill 범위·grpLevel 등)로 만든다.
   *   예전엔 useCallback([editableHeaderSet]) 이라 조회가 끝난 렌더의 값에 묶여,
   *   그 뒤에 도착한 상태(예: 계층 설정 grpLevel 로딩)를 못 보고 활성 상태가 현재 레벨과 어긋날 수 있었다(TC-42).
   *   ag-grid 에는 참조가 고정된 래퍼만 넘기고, 본문은 ref 로 최신 것을 부른다.
   */
  const contextMenuItemsRef = useRef(null);
  contextMenuItemsRef.current = params => {
      const defaultMenuItems = [];
      for(const item of (params?.defaultItems ?? [])) {
        if(item !== "paste" && item !== "pinRowSubMenu"){
          defaultMenuItems.push(item);
        }
      }
      const column = params?.column?.colId;
      let columnHeader = params?.column?.colId;
      const clickNodeData = params?.node?.data;
      // 빈 영역 우클릭 시 node가 없으므로 기본 메뉴만 노출
      if (!clickNodeData) return defaultMenuItems;
      let dateType = false;
      const computeField = clickNodeData.resultRow?.measure?.computeFlg;

      const editableParams = {
        rowData: clickNodeData,
        selectVersion,
        editableMeasure: editableMeasure.current,
        custGrpLevel, itemGrpLevel,
        selectTD: selectTD.current,
        selectCHG, selectIHG,
        selectCL: getGridLevelCd(true),
        selectIL: getGridLevelCd(false)
      };
      clickNodeData.editableRow = WorkbenchEditable.conditionEditable(editableParams);

      const editable = clickNodeData.editableRow && editableHeaderSet.includes(column) && clickNodeData.editableCustomerHrchy && !computeField;

      if (formatGridHeader.includes(columnHeader)) {
        if (!codeUtil.isWeekBucket(selectTD.current)) {
          columnHeader = `${columnHeader}-01`;
        } else {
          columnHeader = reverseHeader.current[columnHeader];
        }
        dateType = true;
      }

      const isLevelColumn = (col) => {
        const isLvCols = [CUST_LEVEL, PROD_LEVEL, CUSTOMER_ATTR, PROD_ATTR, DISPLAY_COLUMN, ATTR_COLUMN, "total"];
        return !isLvCols.some(keyword => col?.includes(keyword)) && ![ITEM_CD_ID, CUST_CD_ID].some(id => isEqual(col, id))
      };

      const cellData = {
        customerHrchy: clickNodeData[CUST_LEVEL_ID],
        itemHrchy: clickNodeData[PROD_LEVEL_ID],
        customerHrchyCd: clickNodeData.resultRow?.customerHrchyCd,
        itemHrchyCd: clickNodeData.resultRow?.itemHrchyCd,
        customerCd: isEqual(customer.cd, selectCL) ? clickNodeData.resultRow?.customerCd : null,
        itemCd: isEqual(item.cd, selectIL) ? clickNodeData.resultRow?.itemCd : null,
        measure: clickNodeData.measure,
        rowMeasure: clickNodeData.resultRow?.measure,
        startDate: filterStartDate,
        endDate: filterEndDate,
        selectTD: selectTD.current,
        pivotData,
        data: clickNodeData.data,
        activeMeasure: measure,
        editableHeaderSet,
        clickNodeData,
        columnHeader,
        dateType,
        custLevel: selectCL,
        itemLevel: selectIL,
        reverseFormatHeaders: reverseHeader.current,
        singleMeasure: isLevelColumn(columnHeader),
        reload: false,
        selectVersion
      };

      defaultMenuItems.push("separator");

      const menuItems = getCustomMenuItems(editable, columnHeader, computeField, cellData);
      return [...defaultMenuItems, ...menuItems];
  };
  const getContextMenuItems = useCallback(params => contextMenuItemsRef.current(params), []);

  const getVersionParams = version => {
    if (size(version) > 0 && version?.planConfigObj) {
      //현재 default version 인 경우는 기본 bucket만 볼 수 있음.
      if (isEqual(version.moduleCd, dpVersion.DEFAULT)) {
        return {
          versionId: version.id,
          startDate: filterStartDate,
          endDate: filterEndDate,
          custLevelCd: customer.cd,
          itemLevelCd: item.cd,
          custFilter: [],
          itemFilter: [],
          singleMeasure: false,
          customerHrchyType: false,
          headerType: true,
          customerFilterList:{},
          itemFilterList: {},
          orderByItem,
          page: 0,
          pageSize: pageSizes[0],
          report: version?.statusCd === versionStatusCode.RELEASED
        };
      } else {
        const { PLAN_START_DATE: planSDate, PLANNING_RANGE: range, BUCKET_UNIT: planBucket, START_DAY: planStartDay } = version?.planConfigObj;
        const startDate = new ZDate(planSDate ?? filterStartDate);
        const endDate = dateUtils.calculateRangeDate(new ZDate(planSDate), planBucket ?? defaultTimeDimension, planStartDay, range ?? planningRange, true);
        const params = {
          versionId: version.id,
          custLevelCd: customer.cd,
          itemLevelCd: item.cd,
          singleMeasure: false,
          customerHrchyType: false,
          headerType: true,
          custFilter: [],
          itemFilter: [],
          timeDimension: planBucket,
          startDate,
          endDate,
          layoutId: null,
          bottomLevel: true,
          planningRange: range,
          customerFilterList:{},
          itemFilterList: {},
          sortType,
          page: 0,
          pageSize: pageSizes[0],
          orderByItem,
          report: version?.statusCd === versionStatusCode.RELEASED
        };
        setPageSize(pageSizes[0]);
        return params;
      }
    }
    return null;
  };

  const checkLayoutFilter = (searchParams) => {
    let clearFlg = false;
    const bottomFilter = ["customerFilterList", "itemFilterList"];
    if (layoutId) {
      for (const [key, value] of entries(searchParams)) {
        if (isEqual(key, "custFilter")) {
          const layoutParam = layoutModalData?.custHrchyList?.map(hrchy => hrchy.hrchyCd) ?? [];

          if (!isEqual(sortBy(value), sortBy(layoutParam))) {
            clearFlg = true;
          }
        }
        if (isEqual(key, "itemFilter")) {
          const layoutParam = layoutModalData?.itemHrchyList?.map(hrchy => hrchy.hrchyCd) ?? [];
          if (!isEqual(sortBy(value), sortBy(layoutParam))) {
            clearFlg = true;
          }
        }
        if (bottomFilter.includes(key)) {
          if (!isEqual(value, layoutModalData[key] ?? {})) {
            clearFlg = true;
          }
        }
        if (isEqual(key, "timeDimension")) {
          if (!isEqual(value, layoutModalData.selectTD)) {
            clearFlg = true;
          }
        }
        if (isEqual(key, "orderByItem")) {
          if (value !== layoutModalData?.orderByItem) {
            clearFlg = true;
          }
        }
      }
      if (clearFlg) {
        clearLayout();
      }
    }
    return clearFlg;
  };

  const searchDemandVal = (specificParams = null) => {
    setColumnDefs([]);
    const params = specificParams ?? getRequestParams();
    const layoutClearFlg = checkLayoutFilter(params);
    retrieveDemandVal(params, layoutClearFlg);
  };

  const retrieveP13n = () => {
    const key = isChannel ? DP_CHANNEL_WORKBENCH_ORDER_ITEM : DP_WORKBENCH_ORDER_ITEM;
    p13nService.get(key)
    .then(response => {
      const { data } = response;
      setOrderByItem(data?.orderByItem ?? false);
    })
    .catch(e => {
      debug(e);
    });
  }

  // z_plan_config(moduleCd=DP, configCd=MEASURE_DISPLAY_NAMES)에 저장된 measure 표시명(별칭) — IP/RP MonthlyByTrend.js/RpPlanVsActual.js와 동일한 패턴.
  const retrieveMeasureDisplayNames = () => {
    planConfigService.getMeasureDisplayNames(codeUtil.module.DP)
      .then(response => {
        const names = response.data?.objVal ?? {};
        displayNameRef.current = names;
      })
      .catch(e => {
        debug(e);
      });
  };

  /*
   * 서버가 준 ATTRIBUTE 는 내용이 같아도 매번 새 객체라, 그대로 setState 하면 레퍼런스가 달라져
   * [dimensionAttrData] 이펙트(:3184)가 무조건 다시 돈다. 그 이펙트는 requestReload() 를 부르고,
   * reload 이펙트(:3130)가 다시 이 함수를 부르므로 **조회가 무한히 반복된다**
   *   (p13n GET 이 demandval POST 보다 빨라 직전 조회 응답이 매번 stale 로 버려지고,
   *    redux 에 dimensionAttrData 를 써 주는 handleLoadData 가 영영 실행되지 않아 탈출 조건이 안 채워진다).
   * → 값이 같으면 이전 레퍼런스를 그대로 유지해 고리를 끊는다.
   */
  const applyDimensionAttrData = next => {
    setDimensionAttrData(prev => (isEqual(prev, next) ? prev : next));
  };

  const retrieveChartConfig = () => {
    p13nService
      .get(pageCd)
      .then(response => {
        if (response.data) {
          setChartP13nData(response.data);
          if (response.data.chart) {
            enabledChartMeasure.current = Object.keys(response.data.chart);
          }
          if (response.data.ATTRIBUTE) {
            applyDimensionAttrData(response.data.ATTRIBUTE);
          } else {
            applyDimensionAttrData({});
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveHrchyGroup = () => {
    const custGrp = [];
    const itemGrp = [];
    hrchyConfigService.getAll()
    .then(response => {
      if (response.data) {
        setGrpLevel(response.data.results)
        for (const result of response.data.results) {
          const row = {
            grpCd : result.grpCd,
            grpName : result.grpName,
            hrchyNum : result.hrchyNum,
          }
          if (result.itemHrchyFlg) {
            itemGrp.push(row);
          } else {
            custGrp.push(row);
          }
        }
        setCustGrpLevel(uniqWith(custGrp, isEqual))
        setSelectCHG(uniqWith(custGrp, isEqual)[0].grpCd)
        setItemGrpLevel(uniqWith(itemGrp, isEqual))
        setSelectIHG(uniqWith(itemGrp, isEqual)[0].grpCd)
      }
    })
    .catch(e => {
      debug(e);
    });
  };

  const handleChangeTimeBucket = (_, newAlignment) => {
    if (!isNull(newAlignment)) {
      if (newAlignment) {
        if (newAlignment === buckets.WEEK && selectVersion?.planConfigObj?.BUCKET_UNIT === buckets.PARTIAL_WEEK) {
          newAlignment = buckets.PARTIAL_WEEK;
        }
        selectTD.current = newAlignment;
        setSelectTDCombo(newAlignment);
        if (selectVersion?.planConfigObj) {
          const { PLAN_START_DATE: planSDate, PLANNING_RANGE: range, BUCKET_UNIT: planBucket, START_DAY: planStartDay } = selectVersion.planConfigObj;
          const from = planSDate ? new ZDate(planSDate) : new ZDate(filterStartDate);
          const to = new ZDate(dateUtils.calculateRangeDate(from, planBucket, planStartDay, range, true));
          setFilterStartDate(dateUtils.nearDay(from, startDay.current, newAlignment));
          setFilterEndDate(addDays(dateUtils.nearDay(to, startDay.current, newAlignment), -1));
        }
        setCurrentPage(1);
        if (pageSizes) {
          setPageSize(pageSizes[0]);
        }
      }
    }
  };

  const handleCheckbox = e => {
    const value = e.target.checked;
    setExcludeEos(value);

    const currentReduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...currentReduxViewState, excludeEos: value }));

    requestReload();
  }

  const handleChartConfigOpen = () => {
    setChartConfigOpen(!chartConfigOpen);
  };

  const saveDemandVal = () => {
    if (saveTarget.length > 0) {
      if (!saveTarget[0].import) {
        setFinishSave(true);
      } else {
        setSnackInfo({open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_TaskCompleteAlert")});
      }

      /*
       * hrchy num 은 조회(searchDemandVal)와 같은 기준으로 구한다.
       *   Advanced Filter 가 다른 그룹(hrchy num≠1)의 레벨을 잡으면 상단 Hrchy Filter(selectCHG/selectIHG) 는 숨겨진 채 옛 그룹에 남는다.
       *   그 값을 쓰면 "그룹 2 레벨 코드 + hrchy num 1" 로 저장이 나가 서버가 leaf 를 못 찾고, 아무것도 안 바뀐 채 성공 응답만 온다.
       *   getGridHrchyNum 은 AF 가 없으면 selectCHG/selectIHG 기준으로 폴백한다.
       */
      const custHrchyNum = getGridHrchyNum(true);
      const itemHrchyNum = getGridHrchyNum(false);

      saveTarget[0].custHrchyNum = custHrchyNum;
      saveTarget[0].itemHrchyNum = itemHrchyNum;

      /* 조회와 같은 이유로 excludeEos 노드는 빼고 보낸다 — 서버가 바인딩할 수 없는 headerName 이다. */
      const advancedFilterTree = stripExcludeEosNodes(getAdvancedFilterTree(advancedFilters));
      saveTarget[0].advancedFilters = hasAdvancedFilterChildren(advancedFilterTree) ? advancedFilterTree : null;
      /*
       * 대신 EOS 제외 값은 조회(params.excludeEos)처럼 플래그로 보낸다 — 빼먹으면 화면에서 뺀 EOS 조합까지 분배돼 level 합과 leaf 합이 어긋난다.
       *   조회(getRequestParams)와 같은 기준: 고급 필터 조건이 있으면 트리의 EOS 노드, 없으면 상단 EOS 제외 체크박스.
       */
      saveTarget[0].excludeEos = hasAdvancedFilterChildren(advancedFilters) ? getExcludeEosFromFilters(advancedFilters) : excludeEos;

      if (!isEmpty(advancedFilters)) {
        if (advancedFilters?.itemLevelCd) {
          saveTarget[0].itemConfigCd = advancedFilters.itemLevelCd;

        }
        if (advancedFilters?.custLevelCd) {
          saveTarget[0].custConfigCd = advancedFilters.custLevelCd;
        }
      }

      demandValService.upsertScenario(saveTarget)
        .then(response => {
          if (response?.data?.message) {
            const { message } = response.data;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message)});
          } else {
            if (response?.data) {
              setSnackInfo({open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess")});
            }
          }
        })
        .catch(e => {
          if (e?.response?.data?.message) {
            const { message } = e.response.data;
            setSnackInfo({open: true, severity: message.type, content: t(message.message)});
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          setSaveTarget([]);
          searchDemandVal();
          setFinishSave(false);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      searchDemandVal();
      setFinishSave(false);
    }
  };

  const confirmPlan = () => {
    setFinishSave(true);
    let postnParams = [];
    if (isArray(myPostn.current)) {
      postnParams = myPostn.current;
    } else {
      postnParams.push({ ...myPostn.current });
    }
    for (const postn of postnParams) {
      postn.editableMeasure = editableMeasure.current;
    }
    postnService
      .confirm(postnParams)
      .then(response => {
        if (response?.data) {
          setFinishSave(false);
          requestReload();
          const { type, message } = response.data;
          setSnackInfo({ open: true, severity: type, content: t(message) });
        }
      })
      .catch(e => {
        setFinishSave(false);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ConfirmFailed") });
        debug(e);
      });
  };

  const handleDialog = action => {
    if (action === "SAVE") {
      saveDemandVal();
    } else if (action === "CONFIRM") {
      confirmPlan();
    }
  };

  const onGridReady = params => {
    setGridApi(params.api);
  };

  const convertValue = e => {
    if (gridApi) {
      const getCellValue = (colId, rowNode) => {
        if (!colId || !rowNode) {
          return null;
        }
        if (typeof e.api?.getValue === "function") {
          return e.api.getValue(colId, rowNode);
        }
        if (typeof gridApi?.getValue === "function") {
          return gridApi.getValue(colId, rowNode);
        }
        return rowNode?.data?.[colId];
      };

      const measure = e.data?.resultRow?.measure;
      const measureCd = measure?.measureCd.slice(0, -3);
      const valueType = measure?.valueTypeCd;
      const itemFlg = e.data?.resultRow?.itemConfigCd === item.cd;

      let avgPrice = null;
      let amtRowNode = null;
      let qtyRowNode = null;
      for (const rowNode of e.node?.parent?.allLeafChildren) {
        const resultRowMeasureCd = rowNode.data?.resultRow?.measure?.measureCd;
        if (isEqual(resultRowMeasureCd, codeUtil.dpMeasure.ASP.cd)) {
          avgPrice = getCellValue(e.column?.colId, rowNode);
        } else if (isEqual(resultRowMeasureCd, measureCd + AMT)) {
          amtRowNode = rowNode;
        } else if (isEqual(resultRowMeasureCd, measureCd + QTY)) {
          qtyRowNode = rowNode;
        }
      }
      if (!avgPrice) {
        return;
      }
      // If it's independent, make it so that only its own measure (excluding pairs) is marked as edited
      if (measure?.disaggTarget === codeUtil.dpDisAggTarget.INDEPENDENT.cd) {
        return;
      }
      if (isEqual(valueType, QTY)) {
        const qty = e.newValue;
        if (amtRowNode) {
          amtRowNode.setDataValue(e.column?.colId, qty * avgPrice);
        }
      } else if (isEqual(valueType, AMT)) {
        const amt = e.newValue;
        if (qtyRowNode) {
          const qtyData = qtyRowNode.data;
          qtyData[e.column?.colId] = Math.floor(amt / avgPrice);
          if (amt % avgPrice !== 0) {
            const curQty = getCellValue(e.column?.colId, qtyRowNode);
            const amtData = amtRowNode.data;
            if (itemFlg) {
              amtData[e.column?.colId] = curQty * avgPrice;
            }
            gridRef.current.api.applyTransaction({update: [amtData, qtyData]});
            gridApi.flashCells({
              rowNodes: [amtRowNode],
              columns: [e.column?.colId],
              flashDelay: 500,
              fadeDelay: 1000,
            });
            if (avgPrice > amt) {
              setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_AMTMoreThanASP") });
            }
          }
        }
      }
    }
  };

  const setChartDatasOnPivotData = (e, headerIndex, target) => {
    const setPivotDataForChart = (pData) => {
      for (const row of pData) {
        if (row.customerHrchyCd === target.customerHrchyCd && row.itemHrchyCd === target.itemHrchyCd && row.measureCd === target.measureCd) {
          if (row.pivotData) {
            row.pivotData[headerIndex] = e.newValue;
          } else {
            const pivotArray = Array.from({ length: formatGridHeader.length }, () => null);
            pivotArray[headerIndex] = e.newValue;
            row.pivotData = pivotArray;
          }
        }
      }
      setChartData(WorkbenchUtil.setChartDatas(pData, formatGridHeader, enabledChartMeasure, editableMeasure, userData?.currencySymbol));
    }

    if (rowPivotData.length > 0) {
      setPivotDataForChart(rowPivotData);
    } else {
      setPivotDataForChart(pivotDataCopy);
    }
  }

  /*
  * 채널 재고 수식 지표(GitHub #44) — POS(POS_QTY)/Market Plan(MF_QTY) 셀을 편집하면 같은 콤보의
  * TARGET_INV_QTY_EOH/SUGGESTED_MP_QTY/BOH/EOH/재고일수(BOH,EOH) 행을 전부 다시 계산해 그리드에
  * 반영한다(WorkbenchUtil.recalcChannelMeasures). TARGET_INV_QTY_EOH/재고일수는 POS_QTY에만 반응하고,
  * SUGGESTED_MP_QTY 를 못 구하는 경우(계층 롤업 등)에만 원본 MF_QTY(T-1)로 fallback한다.
  */
  const recalcChannelInventory = (e) => {
    const editedMeasureCd = e.data?.resultRow?.measureCd;
    if (!["POS_QTY", "MF_QTY"].includes(editedMeasureCd) || !e.node?.parent?.allLeafChildren) {
      return;
    }

    const getCellValue = (colId, rowNode) => {
      if (!colId || !rowNode) {
        return null;
      }
      if (typeof e.api?.getValue === "function") {
        return e.api.getValue(colId, rowNode);
      }
      return rowNode?.data?.[colId];
    };

    const resolveColumnDate = (colId) => {
      const rawDate = codeUtil.isWeekBucket(selectTD.current) ? reverseHeader.current?.[colId] : colId;
      return rawDate ? new ZDate(rawDate) : null;
    };

    const versionStartDate = selectVersion?.planConfigObj?.PLAN_START_DATE
      ? new ZDate(selectVersion.planConfigObj.PLAN_START_DATE)
      : null;

    WorkbenchUtil.recalcChannelMeasures(e.node.parent.allLeafChildren, formatGridHeader, resolveColumnDate, versionStartDate, getCellValue, editedMeasureCd, selectTD.current, startDay.current);
  };

  const getSaveTarget = (e) => {
    if (isNull(e.newValue) || isNaN(e.newValue)) {
      e.newValue = 0;
      e.value = 0;
    }
    /* POS_QTY/MF_QTY(사용자가 직접 입력하는 값)만 음수를 막는다. 이 화면은 채널 재고 계산상
     EOH/BOH 등 파생 지표가 음수로 내려갈 수 있고, 그 값들도 recalcChannelMeasures 가
     setDataValue 로 써서 이 onCellValueChanged 를 그대로 통과한다 — 여기서 무조건 0으로
     clamp 하면 방금 재계산된 음수 EOH/BOH 를 즉시 되돌려버려서(그리고 0은 화면에서 blank 로
     보이므로) "재계산된 셀을 보면 값이 사라진다"는 버그가 났었다. */
    if (["POS_QTY", "MF_QTY"].includes(e.data?.resultRow?.measureCd) && e.newValue < 0 && isChannel) {
      e.newValue = 0;
      e.value = 0;
      e.data[e.column.colId] = 0;
      convertValue(e);
      return;
    }
    if (!isChannel && e.newValue < 0) {
      e.newValue = 0;
      e.value = 0;
      e.data[e.column.colId] = 0;
      convertValue(e);
      return;
    }
    convertValue(e);
    if (isChannel) {
      recalcChannelInventory(e);
    }

    const target = e.data.resultRow;
    target.custConfigCd = getSaveLevelCd(true);
    target.itemConfigCd = getSaveLevelCd(false);
    const targetMeasureCd = target.measureCd;
    const colId = e.column.colId;
    const headerIndex = formatGridHeader.indexOf(colId);

    if (!isNull(e.newValue) && editableMeasure.current.includes(targetMeasureCd)) {
      const newValue = Math.floor(e.newValue);

      if (!target.edited) {
        target.edited = { ...target.edited, [headerIndex]: newValue };
        target.versionDto = selectVersion;
        saveTarget.push({ ...target });
      } else {
        for (const save in saveTarget) {
          if (Object.hasOwn(saveTarget, save)) {
            if (saveTarget[save].customerHrchyCd === target.customerHrchyCd && saveTarget[save].itemHrchyCd === target.itemHrchyCd && saveTarget[save].measureCd === target.measureCd) {
              const key = `${target.customerHrchyCd}${target.itemHrchyCd}${target.measure.measureCd}${colId}`;
              if (dataMap.current[key]) {
                const originalData = dataMap.current[key];
                if (originalData && Number(originalData) === newValue) {
                  delete saveTarget[save].edited[headerIndex];
                } else {
                  saveTarget[save].edited[headerIndex] = newValue;
                }
              } else {
                saveTarget[save].edited[headerIndex] = newValue;
              }
            }
          }
        }
      }
    }
    setChartDatasOnPivotData(e, headerIndex, target);
  }

  const onCellValueChanged = e => {
    if (e.source === "paste" && isString(e.newValue)) {
      const val = parseInt(e.newValue.replace(/[^0-9.]/g, ''), 10);
      e.node.setDataValue(e.column?.colId, val);
      e.newValue = val;
      e.value = val;
    }
    getSaveTarget(e);
  };

  const onCellClicked = e => {
    const filterInfo = extractFilterInfo(e, t, gridRef);
    getFilterStore(viewName).setValue(filterInfo);

    const rowPivot = [];
    for (const row of pivotDataCopy) {
      if (e.data?.[CUST_LEVEL_ID] === row.customerHrchyNm && e.data?.[PROD_LEVEL_ID] === row.itemHrchyNm) {
        rowPivot.push(row);
      }
    }
    setRowPivotData(rowPivot);
    setChartData(WorkbenchUtil.setChartDatas(rowPivot, formatGridHeader, enabledChartMeasure, editableMeasure, userData?.currencySymbol));
    if (formatGridHeader.indexOf(e.column.colId) >= 0) {
      const matrix = [e.rowIndex, formatGridHeader.indexOf(e.column.colId)];
      const target = e.data.resultRow;
      const cellDate = target?.headers[formatGridHeader.indexOf(e.column.colId)];
      target.baseDate = cellDate;
      target.versionDto = selectVersion;
      const cellData = {
        cellDate,
        timeDimension: selectTD.current,
        cellTarget: target,
        cellMatrix: matrix,
        /*
         * NotesModal 은 이 레벨로 "최하위 행이냐" 를 판정해 customerCd / customerHrchyCd 중 하나만 담는다.
         *   Advanced Filter 로 레벨을 올리면 selectCL/selectIL 은 최하위로 남아 있어
         *   집계 행인데도 customerCd(=null) 쪽을 골라 둘 다 null 인 노트가 저장된다 → 그리드 레벨을 넘긴다.
         */
        selectCL: getGridLevelCd(true),
        selectIL: getGridLevelCd(false)
      };
      setWorkbenchCellData(cellData);
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setModelCode(item.name);
      setFilterModalOpen(true);
    }
  };

  const filterActionCustomer = params => {
    if (params.type === "click") {
      setModelCode(customer.name);
      setFilterModalOpen(true);
    }
  };

  const filterActionHrchyGroup = params => {
    if (params.type === "click") {
      setFilterEl(params.currentTarget);
      setHrchyGroupOpen(!hrchyGroupOpen);
    }
  };

  /*
   * 화면 초기화 — 상단 RESET 버튼과 고급 필터의 "전체 삭제" 가 공유한다(handleAdvancedFilterRemoveAll).
   *   keepFilterPanelOpen: 고급 필터 패널 **안에서** 호출된 경우엔 패널을 닫지 않는다.
   */
  const handleReset = ({ keepFilterPanelOpen = false } = {}) => {
    const versionParams = getVersionParams(selectVersion);
    clearLayout();
    setSelectCL(customer.cd);
    setSelectIL(item.cd);
    setSelectIHG(itemGrpLevel[0]?.grpCd);
    setSelectCHG(custGrpLevel[0]?.grpCd);
    setCustFilter([]);
    setItemFilterList({});
    setCustomerFilterList({});
    setCustFilterType([filterType.or.cd, filterType.equal.cd]);
    setItemFilterType([filterType.or.cd, filterType.equal.cd]);
    setItemFilter([]);
    setInvClassCdList([]);
    setDisabledInvClass(false);
    setExcludeEos(false);
    setSaveTarget([]);
    setCurrentPage(1);
    setLayoutId(null);
    setCustomValue([]);
    setItemCustomValue([]);
    setFilterValue({});
    setCellInfo({});
    setSortParams({});
    setTreeload(true);
    setItemTreeLoad(true);
    setPageSize(reduxViewState?.pageSize ?? pageSizes[0]);
    setAdvancedFilters(null);
    setOpenFilter(keepFilterPanelOpen);
    if (versionParams !== null) {
      selectTD.current = versionParams.timeDimension;
      setSelectTDCombo(versionParams?.timeDimension);
      setFilterStartDate(versionParams.startDate);
      setFilterEndDate(versionParams.endDate);
    }
    isDrillSingleMeasure.current = false;
    drillMeasure.current = null;
    if (reduxViewState) {
      reduxViewState.itemLevelList = itemLevel;
      reduxViewState.custLevelList = custLevel;
      reduxViewState.custLevelCd = customer.cd;
      reduxViewState.itemLevelCd = item.cd;
      reduxViewState.custGrpLevelCd = custGrpLevel[0]?.grpCd;
      reduxViewState.itemGrpLevelCd = itemGrpLevel[0]?.grpCd;
      reduxViewState.custGrpLevelList = custGrpLevel;
      reduxViewState.itemGrpLevelList = itemGrpLevel;
      reduxViewState.grpLevelList = grpLevel;
      reduxViewState.custFilter = [];
      reduxViewState.customerFilterList = {};
      reduxViewState.itemFilter = [];
      reduxViewState.itemFilterList = {};
      reduxViewState.itemFilterType = [filterType.or.cd, filterType.equal.cd];
      reduxViewState.custFilterType = [filterType.or.cd, filterType.equal.cd];
      reduxViewState.invClassCdList = [];
      /* excludeEos 는 redux 에서 초기값을 읽어 오므로(useState) 여기서 같이 지우지 않으면 다음 진입에 되살아난다. */
      reduxViewState.excludeEos = false;
      reduxViewState.layoutId = "";
      reduxViewState.layoutModalData = [];
      reduxViewState.filter = {};
      reduxViewState.customerFilterList= {};
      reduxViewState.itemFilterList= {};
      reduxViewState.timeDimension = versionParams?.timeDimension ?? "month";
      reduxViewState.startDate = versionParams?.startDate;
      reduxViewState.endDate = versionParams?.endDate;
      reduxViewState.isDrillSingleMeasure = false;
      reduxViewState.drillMeasure = null;
      reduxViewState.advancedFilters = null;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
  };

  /*
   * 첫 진입(저장된 버킷 없음)에서 버전이 정해지면 버킷·주 시작 요일·기간을 그 버전 기준으로 맞춘다(TC-64).
   *   맞추지 않으면 화면 상태로 만드는 조회(getRequestParams)가 초기 기본값(month, startDay null, 이번 달)으로 나가
   *   Partial Week 버전도 month 로 조회되고, 그 응답이 버킷을 month 로 덮었다.
   */
  const syncBucketWithVersion = () => {
    const versionParams = getVersionParams(selectVersion);
    if (!versionParams?.timeDimension) {
      return;
    }
    selectTD.current = versionParams.timeDimension;
    setSelectTDCombo(versionParams.timeDimension);
    startDay.current = selectVersion.planConfigObj?.START_DAY ?? startDay.current;
    setFilterStartDate(versionParams.startDate);
    setFilterEndDate(versionParams.endDate);
    reduxViewState.timeDimension = versionParams.timeDimension;
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
  };

  const clickReset = () => {
    handleReset();
    requestReload();
  }

  const getDefaultCustomization = () => {
    p13nService.getDefaultLayout(viewName)
    .then(response => {
      const { data } = response;
      if (data?.layoutId) {
        if (selectVersion?.planConfigObj) {
          const { PLAN_START_DATE: planSDate, PLANNING_RANGE: range, BUCKET_UNIT: planBucket, START_DAY: planStartDay } = selectVersion.planConfigObj;
          if (planSDate) {
            setFilterStartDate(new ZDate(planSDate));
            const endDate = dateUtils.calculateRangeDate(new ZDate(planSDate), planBucket ?? defaultTimeDimension, planStartDay, range ?? planningRange, true);
            if (endDate) {
              setFilterEndDate(endDate)
            }
          }
        }
        setLayoutModalData(data);
      } else {
        searchDemandVal(getVersionParams(selectVersion));
      }
      })
      .catch(e => {
      searchDemandVal(getVersionParams(selectVersion));
      debug(e);
    })
  };

  const getCustomConfig = () => {
    companyService
      .getCustomConfig()
      .then(response => {
        if (response.data) {
          setAttrConfig(response.data);
        } else {
          setAttrConfig({})
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const filterCheck = (customerHrchy, itemHrchy) =>{
    let filterFlg = false;
    if (isEmpty(filterValue) && reload) {
      filterFlg = true;
    }
    if (!isEmpty(customerHrchy) && (reload || !isEqual(customValue, customerHrchy))) {
      filterFlg = true;
    }
    if (!isEmpty(itemHrchy) && (reload || !isEqual(itemCustomValue, itemHrchy))) {
      filterFlg = true;
    }
    if ((!isEmpty(itemHrchy) || !isEmpty(customerHrchy))) {
      if (reload) {
        filterFlg = false;
      }
    }
    return filterFlg;
  }

  const compareHrchys = (org, pre) => {
    const orgList = cloneDeep(org)?.map((hrchy) => hrchy.hrchyCd);
    const preList = cloneDeep(pre)?.map((hrchy) => hrchy.hrchyCd);
    return isEqual(orgList, preList);
  }

  useEffect(() => {
    if (Object.keys(updateNoteData).length > 0) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName: DP_WORKBENCH_GRID});
      setUpdateNoteData({})
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNoteData]);

  // 일괄 가져오기 및 저장 완료 시 (NotificationBell이 감지 후 dispatch) 현재 조회 조건으로 재조회
  useEffect(() => {
    const handleImportBulkCompleted = () => {
      searchDemandVal();
    };
    window.addEventListener('plannel:importBulkCompleted', handleImportBulkCompleted);
    return () => window.removeEventListener('plannel:importBulkCompleted', handleImportBulkCompleted);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 현재 화면을 viewState 에 등록. 렌더 도중 dispatch 하면
    // "Cannot update a component while rendering" 경고와 함께 리렌더 churn 이 생겨
    // 다른 컴포넌트(App)의 상태 업데이트(탭 전환 등)가 유실될 수 있어 mount 시 1회만 실행한다.
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
    if (reduxViewState) {
      if (!reduxViewState?.custGrpLevelCd || !reduxViewState?.itemGrpLevelCd) {
        retrieveHrchyGroup();
      }
    } else {
      retrieveHrchyGroup();
    }
    retrieveChartConfig();
    getCustomConfig();
    if (isNull(orderByItem)) {
      retrieveP13n();
    }
    retrieveMeasureDisplayNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(reduxObjWorkbench).length > 0) {
      const alert = setInterval(() => {
        setReduxObjWorkbench(reduxUtil.getViewState(viewName));
      }, 5000);
      if (reduxObjWorkbench.endMakeDemand || !reduxObjWorkbench.makeDemand) {
        setDisabledSave(false);
        clearInterval(alert);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxObjWorkbench]);

  useEffect(() => {
    const dpProcessRedux = reduxUtil.getViewState("DP_PROCESS_MGMT");
    if (dpProcessRedux) {
      if (dpProcessRedux.makeDemand) {
        setDisabledSave(true);
        setReduxObjWorkbench(reduxUtil.getViewState(viewName));
      } else {
        setDisabledSave(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading) {
      if (reduxViewState?.selectVersion) {
        searchDemandVal();
      } else {
        getDefaultCustomization();
      }
    }
    return () => {
      setSaveTarget([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (bulkValueNodeData?.reload) {
      searchDemandVal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkValueNodeData]);

  useEffect(() => {
    if (custLevel?.length > 0) {
      if (reduxViewState) {
        reduxViewState.custLevelList = custLevel;
      }
      if (reduxViewState?.custLevelCd && reduxViewState?.custGrpLevelCd === selectCHG) {
        setSelectCL(reduxViewState.custLevelCd);
        setSelectCHG(reduxViewState?.custGrpLevelCd)
      } else {
        setSelectCL(custLevel[custLevel.length - 1].cd);
      }
    }

    if (itemLevel?.length > 0) {
      if (reduxViewState) {
        reduxViewState.itemLevelList = itemLevel;
      }
      if (reduxViewState?.itemLevelCd && reduxViewState?.itemGrpLevelCd === selectIHG) {
        setSelectIL(reduxViewState.itemLevelCd);
        setSelectIHG(reduxViewState?.itemGrpLevelCd)
      } else {
        setSelectIL(itemLevel[itemLevel.length - 1].cd);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custLevel, itemLevel]);

  useEffect(() => {
    if (reduxViewState && selectCL) {
      if (!isEqual(reduxViewState?.custLevelCd, selectCL) && !drill.current) {
        if (selectCL === customer.cd) {
          setCustFilter([]);
        } else {
          setCustomerFilterList({});
        }
        // setCellInfo({});
        reduxViewState.custLevelCd = selectCL;
        reduxViewState.cellInfo = {};
      }
    }
    setLoadingCondition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCL]);

  useEffect(() => {
    const customerHrchy = filterValue?.customerHrchys;
    const itemHrchy = filterValue?.category;
    let filterFlg = filterCheck(customerHrchy, itemHrchy);
    if (!isLevel.current) {
      if (size(customerHrchy) > 0) {
        setSelectCL(customerHrchy[0]?.hrchyLv);
        if (reduxViewState?.custLevelCd && !isEqual(reduxViewState.custLevelCd, customerHrchy[0]?.hrchyLv)) {
          filterFlg = true;
        }
        setCustomerFilterList({});
      } else {
        setSelectCL(customer.cd);
        if (reduxViewState?.custLevelCd && !isEqual(reduxViewState.custLevelCd, customer.cd)) {
          filterFlg = true;
        }
      }
      if (size(itemHrchy) > 0) {
        setSelectIL(itemHrchy[0]?.hrchyLv);
        if (reduxViewState?.itemLevelCd && !isEqual(reduxViewState.itemLevelCd, itemHrchy[0]?.hrchyLv)) {
          filterFlg = true;
          setInvClassCdList([]);
          setDisabledInvClass(true);
        }
        setItemFilterList({});
      } else {
        setSelectIL(item.cd);
        if (reduxViewState?.itemLevelCd && !isEqual(reduxViewState.itemLevelCd, item.cd)) {
          filterFlg = true;
        }
      }
    }
    if (!compareHrchys(itemHrchy, itemCustomValue)) {
      setItemFilter(itemHrchy?.map(el => el.hrchyCd) || []);
      setItemCustomValue(itemHrchy);
    } else if (!compareHrchys(customerHrchy, customValue)) {
      setCustFilter(customerHrchy?.map(el => el.hrchyCd) || []);
      setCustomValue(customerHrchy);
    } else {
      filterFlg = false;
    }
    if (!drill.current && Object.keys(cellInfo).length === 0 && reload) {
      requestReload();
    }
    if (!drill.current && reduxViewState && filterFlg) {
      requestReload();
    }
    if (drill.current) {
      drill.current = false;
    }
    if (isLevel.current) {
      isLevel.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  useEffect(() => {
    if (reduxViewState && selectIL) {
      if (!isEqual(reduxViewState?.itemLevelCd, selectIL) && !drill.current) {
        if (selectIL === item.cd) {
          setItemFilter([]);
        } else {
          setItemFilterList({});
        }
        setCellInfo({});
        reduxViewState.itemLevelCd = selectIL;
        reduxViewState.cellInfo = {};
      }
    }
    setLoadingCondition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectIL]);

  useEffect(() => {
    if (grpLevel) {
      const data = grpLevel.filter((el) => el.grpCd === selectIHG);
      setSelectedItemHrchyNum(data[0]?.hrchyNum || codeUtil.hrchyNum.HRCHY);

      if (!isEqual(reduxViewState?.itemGrpLevelCd, selectIHG)) {
        setItemTreeLoad(true);
        requestReload();
        setFilterValue((prev)=> ({ ...prev, category: []}));
      }
      setItemLevel([...data, item]);
      setSelectIL(item.cd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectIHG]);

  useEffect(() => {
    if (grpLevel) {
      const data = grpLevel.filter((el) => el.grpCd === selectCHG);
      setSelectedHrchyNum(data[0]?.hrchyNum || codeUtil.hrchyNum.HRCHY);

      if (!isEqual(reduxViewState?.custGrpLevelCd, selectCHG)) {
        setTreeload(true);
        requestReload();
        setFilterValue((prev) => ({ ...prev, customerHrchys: []}));
      }
      setCustLevel([...data, customer]);
      setSelectCL(customer.cd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCHG]);

  /*
   * 첫 렌더의 selectTDCombo 는 사용자가 고른 값이 아니다 — 버전이 아직 없으면 기본값(month)이다.
   *   이걸 redux 에 쓰고 재조회를 걸면 Partial Week 버전도 month 로 조회됐다(TC-64). 버킷은 버전 로드 때 맞춘다.
   */
  const isInitialTDCombo = useRef(true);
  useEffect(() => {
    if (isInitialTDCombo.current) {
      isInitialTDCombo.current = false;
      return;
    }
    if (reduxViewState && selectTD.current) {
      if (!isEqual(reduxViewState?.timeDimension, selectTDCombo)) {
        requestReload();
        reduxViewState.timeDimension = selectTDCombo;
        selectTD.current = selectTDCombo;
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectTDCombo]);

  useEffect(() => {
    if (reduxViewState?.custFilter) {
      if (!isEqual(reduxViewState?.custFilter, custFilter)) {
        requestReload();
        setCellInfo({});
      }
      reduxViewState.custFilter = custFilter;
      //reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custFilter]);

  useEffect(() => {
    if (reduxViewState?.itemFilter) {
      if (!isEqual(reduxViewState?.itemFilter, itemFilter)) {
        requestReload();
        setCellInfo({});
      }
      reduxViewState.itemFilter = itemFilter;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFilter]);

  useEffect(() => {
    if (reduxViewState?.invClassCdList) {
      if (!isEqual(reduxViewState?.invClassCdList, invClassCdList)) {
        requestReload();
        setCellInfo({});
      }
    }
    if (reduxViewState) {
      reduxViewState.invClassCdList = invClassCdList;
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invClassCdList]);

  useEffect(() => {
    if (itemFilterList && Object.keys(itemFilterList)?.length > 0) {
      setItemFilterColor(muicolor.INFO);
    } else {
      setItemFilterColor(muicolor.DEFAULT);
    }

    if (customerFilterList && Object.keys(customerFilterList)?.length > 0) {
      setCustomerFilterColor(muicolor.INFO);
    } else {
      setCustomerFilterColor(muicolor.DEFAULT);
    }

    if (reduxViewState) {
      if (!drill.current && (!isEqual(reduxViewState?.itemFilterList, itemFilterList) || !isEqual(reduxViewState?.itemFilterType, itemFilterType) ||
      !isEqual(reduxViewState?.customerFilterList, customerFilterList) || !isEqual(reduxViewState?.custFilterType, custFilterType))) {
        if (cellInfo?.title === "DRILL_UP") {
          setReload(false);
        } else {
          if (Object.keys(cellInfo)?.length === 0) {
            requestReload();
          }
        }
      }
    }
    setCellInfo({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemFilterList, customerFilterList, custFilterType, itemFilterType]);

  useEffect(() => {
    if (gridApi && isLoading && reduxViewState && !reload) {
      if (!isEqual(reduxViewState?.currentPage, currentPage) || !isEqual(reduxViewState?.pageSize, pageSize) || (!isEqual(reduxViewState?.sortParams, sortParams))) {
        searchDemandVal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortParams]);

  useEffect(() => {
    if (gridApi) {
      DataState.initialize(gridApi);
    }
  }, [gridApi]);

  // recharts ChartComposed 용 series. chartData/설정 변경 시 재계산.
  const chartSeries = useMemo(
    () => WorkbenchUtil.chartDataSeries(chartP13nData, chartData, enabledChartMeasure, measure, isChannel),
    // measure 명이 gridValueL10N 으로 번역되므로 언어가 바뀌면 다시 만들어야 한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartData, chartP13nData, measure, t, isChannel]
  );

  // ChartConfigModal 의 measureService.getAll 목록에는 없는 채널 재고 수식 지표를 보충.
  const chartAdditionalMeasures = useMemo(
    () => CHART_ONLY_CHANNEL_MEASURE_CDS
      .map((cd) => measure[cd])
      .filter((m) => Boolean(m)),
    [measure]
  );


  useEffect(() => {
    if (layoutModalData?.reloadFalse) {
      setReload(false);
      return;
    }

    if (layoutModalData?.clearLayout) {
      handleReset();
      requestReload();
    }

    if (!layoutModalData?.reloadFalse && size(layoutModalData) > 2) {
      setReload(false);
      const layoutTD = layoutModalData.selectTD ?? defaultTimeDimension;
      const layoutCustFilter = layoutModalData.custFilter ?? [];
      const layoutItemFilter = layoutModalData.itemFilter ?? [];
      const layoutSelectCL = layoutModalData.selectCL?.cd;
      const layoutselectIL = layoutModalData.selectIL?.cd;
      const layoutSelectCHG = layoutModalData.selectCHG?.grpCd;
      const layoutselectIHG = layoutModalData.selectIHG?.grpCd;

      let layoutCustFilterType = layoutModalData.custFilterType;
      let layoutItemFilterType = layoutModalData.itemFilterType;
      const layoutInvClassCd = layoutModalData.invClassCdList ?? [];

      //layoutData cust/item filter type 확인
      //기존 저장된 데이터는 string / list로 변환 필요
      if (layoutCustFilterType && typeof layoutCustFilterType === "string") {
        // if ([filterType.or.cd, filterType.equal.cd])
        if (layoutCustFilterType === filterType.equal.cd) {
          layoutCustFilterType = [filterType.or.cd, filterType.equal.cd];
        } else if (layoutCustFilterType === filterType.notEqual.cd) {
          layoutCustFilterType = [filterType.and.cd, filterType.notEqual.cd];
        }
      }
      if (layoutItemFilterType && typeof layoutItemFilterType === "string") {
        // if ([filterType.or.cd, filterType.equal.cd])
        if (layoutItemFilterType === filterType.equal.cd) {
          layoutItemFilterType = [filterType.or.cd, filterType.equal.cd];
        } else if (layoutItemFilterType === filterType.notEqual.cd) {
          layoutItemFilterType = [filterType.and.cd, filterType.notEqual.cd];
        }
      }
      setSelectCL(layoutSelectCL);
      setSelectIL(layoutselectIL);
      setSelectCHG(layoutSelectCHG);
      setSelectIHG(layoutselectIHG);
      setOrderByItem(layoutModalData.orderByItem);
      setItemCustomValue(layoutModalData?.itemHrchyList ?? []);
      setCustomValue(layoutModalData?.custHrchyList ?? []);
      if (isEmpty(layoutModalData?.custHrchyList ?? [])) {
        setTreeload(true);
      }
      if (isEmpty(layoutModalData?.itemHrchyList ?? [])) {
        setItemTreeLoad(true);
      }
      setCustomerFilterList(layoutModalData.customerFilterList ?? {});
      setCustFilterType(layoutCustFilterType ?? [filterType.or.cd, filterType.equal.cd]);
      setCustFilter(layoutCustFilter);
      setItemFilterList(layoutModalData.itemFilterList ?? {});
      setItemFilterType(layoutItemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
      setItemFilter(layoutItemFilter);
      setInvClassCdList(layoutInvClassCd);
      if (!isEqual(layoutTD, selectTD.current)) {
        const versionParams = getVersionParams(selectVersion);
        if (versionParams !== null) {
          setFilterStartDate(versionParams.startDate);
          setFilterEndDate(versionParams.endDate);
        }
      }
      setSelectTDCombo(layoutTD);
      selectTD.current = layoutTD;
      // if (layoutModalData?.periodRange && layoutModalData?.periodRange !== 0) {
      //   Date = (target, bucket, range) => {
      //   dateUtils.calRangeDate(selectVersion)
      // }
      setLayoutId(layoutModalData.layoutId);
      setAppliedLayout(layoutModalData.layoutId);

      if (reduxViewState) {
        reduxViewState.itemLevelList = itemLevel;
        reduxViewState.custLevelList = custLevel;
        reduxViewState.custLevelCd = layoutSelectCL;
        reduxViewState.itemLevelCd = layoutselectIL;
        reduxViewState.custGrpLevelCd = layoutSelectCHG;
        reduxViewState.itemGrpLevelCd = layoutselectIHG;
        reduxViewState.custFilter = layoutCustFilter;
        reduxViewState.customerFilterList = layoutModalData.customerFilterList ?? {};
        reduxViewState.custFilterType = layoutModalData?.custFilterType ?? [filterType.or.cd, filterType.equal.cd];
        reduxViewState.itemFilter = layoutItemFilter;
        reduxViewState.itemFilterList = layoutModalData.itemFilterList ?? {};
        reduxViewState.itemFilterType = layoutModalData?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
        reduxViewState.timeDimension = layoutTD;
        reduxViewState.invClassCdList = layoutInvClassCd;
        reduxViewState.layoutId = layoutModalData.layoutId;
        reduxViewState.layoutModalData = { ...layoutModalData, reloadFalse: false };
        reduxViewState.orderByItem = layoutModalData.orderByItem;
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
      }
      // After the conditions are set
      setTimeout(() => {
        requestReload();
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutModalData]);

  useEffect(() => {
    if (layoutId) {
      clearLayout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalData])

  /*
   * 조회 합류 — requestReload() 는 한 동작에서 여러 번 불린다.
   *   계층 레벨/그룹을 바꾸면 selectIHG·selectCHG → filterValue → itemFilter·custFilter 가
   *   **한 커밋이 아니라 서너 커밋에 걸쳐** 갱신된다.
   * 이 타이머는 "지금 바로 던져도 되는" reload 요청만 한 발로 합친다.
   *   이미 조회가 진행 중이면 requestReload() 는 nonce 대신 queuedReloadRef 만 세워 두고,
   *   최신 응답이 끝난 뒤 한 번만 다시 올린다. 그래서 화면 로딩이 두 번 겹치지 않는다.
   */
  useEffect(() => {
    if (!reduxViewState) {
      return undefined;
    }
    if (retrieveInFlightRef.current || !(isLoading && reload && selectCL !== "" && selectIL !== "" && size(selectVersion) > 0)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      retrieveChartConfig();
      searchDemandVal();
    }, RELOAD_COALESCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadNonce]);

  useEffect(() => {
    if (reduxViewState) {
      if (isLoading && !isEqual(reduxViewState?.selectVersion, selectVersion)) {
        handleReset();
        searchDemandVal(getVersionParams(selectVersion));
      } else if (!isLoading && !reduxViewState.timeDimension) {
        syncBucketWithVersion();
      }
    }
    setLoadingCondition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectVersion]);

  useEffect(() => {
    if (filterData.filterList) {
      if (filterData.modelCode === customer.name) {
        setCustomerFilterList(filterData.filterList ?? {});
        setFilterValue({ ...filterValue, customerHrchys: [], customerCheckSelected: []});
        setCustomValue([])
        setTreeload(true);
        setCustFilterType(filterData.filterType)
      } else {
        setItemFilterList(filterData.filterList ?? {});
        setFilterValue({ ...filterValue, category: []});
        setItemCustomValue([]);
        setItemTreeLoad(true);
        setItemFilterType(filterData.filterType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    if (chartModalResultData.reload) {
      setChartP13nData(chartModalResultData.content);
      enabledChartMeasure.current = Object.keys(chartModalResultData?.content?.chart);
      if (enabledChartMeasure.current.length === 0) {
        setChartData([]);
      } else {
        setChartData(WorkbenchUtil.setChartDatas(pivotDataCopy, formatGridHeader, enabledChartMeasure, editableMeasure, userData?.currencySymbol));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartModalResultData]);

  useEffect(() => {
    if (isLoading && !isUndefined(reduxViewState?.dimensionAttrData)) {
      if (!isEqual(reduxViewState?.dimensionAttrData, dimensionAttrData) && !isNull(dimensionAttrData)) {
        setSortParams({});
        requestReload();
      }
    }
    setLoadingCondition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionAttrData]);

  const modalClose = () => {
    setModalOpen(false);
    if (modalData) {
      if (modalData.reload) {
        requestReload();
        clearLayout();
      }
    }
  };

  useEffect(() => {
    if (columnDefs.length > 0) {
      if (isLoading) {
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName: DP_WORKBENCH_GRID, initState: true});
        const currentReduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...currentReduxViewState, columnDefs }));
      } else {
        GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName: DP_WORKBENCH_GRID});
      }
    }
 // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs]);

  /*
   * 세 패널이 같은 줄에 나란히 서므로 서로를 닫을 이유가 없다.
   *   예전에는 이것만 그리드 위에 떠 있어서 겹치지 않게 노트를 강제로 닫았다.
   */
  const handleLayoutOpen = () => {
    setLayoutOpen(!layoutOpen);
  };

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbench, viewName]);

  useEffect(() => {
    // 고급 필터를 새로 적용하거나 지우면 그 위에서 한 drill / 레벨 이동은 버리고 필터의 레벨로 돌아간다.
    advancedFilterDrillLevel.current = null;
    if (advancedFilters != null) {
      let clearedLegacyFilter = false;
      if (hasAdvancedFilterChildren(advancedFilters)) {
        setAdvancedFilterColor("info");

        // advanced filter 활성화 시 기존 필터 초기화
        setCustFilter([]);
        setItemFilter([]);
        setItemFilterList({});
        setCustomerFilterList({});
        setCustFilterType([filterType.or.cd, filterType.equal.cd]);
        setItemFilterType([filterType.or.cd, filterType.equal.cd]);
        setInvClassCdList([]);
        setCustomValue([]);
        setItemCustomValue([]);
        setFilterValue({});
        setTreeload(true);
        setItemTreeLoad(true);
        /* Exclude EOS 도 고급 필터 조건으로 이사했으므로(초기 조건) 상단 체크박스는 소유권을 내려놓는다. */
        setExcludeEos(false);

        if (reduxViewState && !isEmpty(reduxViewState.filter)) {
          reduxViewState.filter = {};
          reduxViewState.custFilter = [];
          reduxViewState.itemFilter = [];
          reduxViewState.customerFilterList = {};
          reduxViewState.itemFilterList = {};
          reduxViewState.invClassCdList = [];
          clearedLegacyFilter = true;
        }

        /* excludeEos 는 useState 초기값을 redux 에서 읽으므로, 여기서 지우지 않으면 다음 진입에 되살아난다. */
        if (reduxViewState?.excludeEos) {
          reduxViewState.excludeEos = false;
          clearedLegacyFilter = true;
        }
      } else {
        setAdvancedFilterColor("default");
      }

      if (hasAdvancedFilterLevel(advancedFilters)) {
        advancedFilterOwnsLevel.current = true;
      } else if (advancedFilterOwnsLevel.current) {
        advancedFilterOwnsLevel.current = false;
        setSelectCL(customer.cd);
        setSelectIL(item.cd);
        // 고급 필터 레벨 조회 때 상단 필터에 남았을 수 있는 계층 값도 같이 비운다(handleLoadData plantHrchyFilter 참고).
        setCustomValue([]);
        setItemCustomValue([]);
        setFilterValue({});
        setTreeload(true);
        setItemTreeLoad(true);
        if (reduxViewState
          && (!isEqual(reduxViewState.custLevelCd, customer.cd) || !isEqual(reduxViewState.itemLevelCd, item.cd))) {
          reduxViewState.custLevelCd = customer.cd;
          reduxViewState.itemLevelCd = item.cd;
          clearedLegacyFilter = true;
        }
      }

      if (reduxViewState && (clearedLegacyFilter || !isEqual(reduxViewState?.advancedFilters, advancedFilters))) {
        reduxDispatch(reduxUtil.updateViewState({...reduxState, ...reduxViewState, advancedFilters}));
      }

      if (isLoading) {
        setCellInfo({});
        setCurrentPage(1);
        requestReload();
      }
    } else {
    // advancedFilters가 null로 초기화된 경우 색상 리셋
    setAdvancedFilterColor("default");
    /* clickReset 이 레벨까지 직접 최하위로 되돌리므로 여기서는 소유권 표시만 내려놓는다. */
    advancedFilterOwnsLevel.current = false;
  }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters])

   /*
    * Advanced Filter panel이 열려있을 때는 계층필터 변경이 영향을 주지 않는다.
    * 오직 panel이 닫혀있다가 열릴 때만 계층필터 정보를 Advanced Filter 초기 조건으로 반영한다.
    */
   useEffect(() => {
     const wasClosedNowOpen = !prevOpenFilterRef.current && openFilter;
     prevOpenFilterRef.current = openFilter;

     if (wasClosedNowOpen) {
       setAdvancedFilterInitialConditions(hasAdvancedFilterChildren(advancedFilters) ? null : advancedFilterLegacyConditions);
     } else if (!openFilter) {
       setAdvancedFilterInitialConditions(null);
     }

     if (wasClosedNowOpen && !isEmpty(filterValue)) {
       // Panel이 닫혀있다가 지금 열렸고, 계층필터가 적용되어 있으면
       if (hasAdvancedFilterChildren(advancedFilters)) {
         return; // 이미 Advanced Filter 조건이 있으면 리셋 안 함
       }
       setAdvancedFilters(null); // null로 리셋하여 새로운 초기조건 반영
     }
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [openFilter]);

   /*
    * Advanced Filter panel이 닫혀있을 때만 계층필터 변경에 반응한다.
    * panel이 열려있으면 계층필터 변경을 무시한다.
    */
   useEffect(() => {
     if (openFilter) {
       // Panel이 열려있으면 무시
       return;
     }

     // Panel이 닫혀있으면 filterValue 변경 감지 (현재는 특별한 처리 없음)
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [filterValue, openFilter]);

   const handleAdvancedFilterApply = (filters) => {
     const baseDateRange = getAdvancedFilterBaseDateRange(filters);
     if (baseDateRange) {
       setFilterStartDate(baseDateRange.startDate);
       setFilterEndDate(baseDateRange.endDate);
     }
     /* Confirm 이후엔 사용자가 만든 트리가 원본이다. 스냅샷을 남겨 두면 조건을 모두 지우고 Confirm 했을 때 옛 계층 필터가 되살아난다. */
     setAdvancedFilterInitialConditions(null);
     setAdvancedFilters(filters);
   };

   const handleAdvancedFilterRemoveAll = () => {
     setAdvancedFilterInitialConditions(null);
     handleReset({ keepFilterPanelOpen: true });
     requestReload();
   };

   const collapseFilter = _event => {
     setOpenFilter(!openFilter);
   };

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getColumnDate = (columnId) => {
    return new ZDate(columnId);
  }

  const setImportDatas = importDatas => {
    try {
      if (size(importDatas) > 0 && gridApi) {
        const importSaveTarget = importDatas.map(tar => {
          tar.custConfigCd = getSaveLevelCd(true);
          tar.itemConfigCd = getSaveLevelCd(false);
          tar.import = true;
          return tar;
        });
        const filterdSaveTarget = importSaveTarget.filter(tar => tar.editable && tar.editableCustomerHrchy);
        setSaveTarget(filterdSaveTarget);

        for (const target of importDatas) {
          gridApi.forEachNode(node => {
            if (node?.data?.resultRow) {
              const { key, measureCd: cellMeasureCd } = node.data.resultRow;
              if (target?.editableCustomerHrchy && isEqual(key, target.key) && isEqual(cellMeasureCd, target.measureCd) && size(target.edited) > 0) {
                for (const property of Object.keys(target.edited)) {
                  const columnId = target.headers[property];
                  if (columnId && selectTDCombo) {
                    const columnDate = getColumnDate(columnId);
                    let formatColId = dateUtils.headerDateFormatter(columnDate, selectTDCombo);
                    if (codeUtil.isWeekBucket(selectTDCombo)) {
                      formatColId = weekFormatHeader.current[formatColId];
                    }
                    node.setDataValue(formatColId, target.edited[property]);
                  }
                }
              }
            }
          });
        }
      }
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SuccessAndApply") });
    } catch (error) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: error });
    }
  };

  const setHrchyGroup = (hrchyGroups) => {
    if (!isUndefined(hrchyGroups.chg) && !isEqual(hrchyGroups.chg, "")) {
      setSelectCHG(hrchyGroups.chg);
    }
    if (!isUndefined(hrchyGroups.ihg) && !isEqual(hrchyGroups.ihg, "")) {
      setSelectIHG(hrchyGroups.ihg);
    }
  }

  const saveOrderByItem = (orderByItemParam) => {
    const page = isChannel ? DP_CHANNEL_WORKBENCH_ORDER_ITEM : DP_WORKBENCH_ORDER_ITEM;
    const saveData = { pageCd: page, configObj: { orderByItem: orderByItemParam } };

    p13nService.upsert(saveData)
      .then(() => {
        debug(t("MSG_SaveSuccess"));
      })
      .catch(e => {
        debug(e);
      });
  }

  const changeSortDimen = () => {
    setOrderByItem(!orderByItem);
    searchDemandVal(getRequestParams(!orderByItem));
    saveOrderByItem(!orderByItem);
  }

  const getSortState = (params) => {
    const columnState = params.api.getColumnState();
    let sortState = sortParams ?? {};
    const sortColumn = find(columnState, column => isEqual(column.sort, "asc") || isEqual(column.sort, "desc"));
    if (isEqual(params.source, "uiColumnSorted")) {
      let sortColNm = sortColumn?.colId;
      if (includes(sortColumn?.colId, ITEM)) {
        sortColNm = sortColumn.colId.replace(ITEM, "prod_");
      } else if (includes(sortColumn?.colId, "cust") && !includes(sortColumn?.colId, "_")) {
        sortColNm = sortColumn.colId.replace("cust", "cust_");
      }

      sortState = { orderByColumn: sortColNm, sortType: sortColumn?.sort };
    }
    setSortParams(sortState);
  }

  const settingSortParams = (params) => {
    if (!dimenModalOpen) {
      getSortState(params);
    }
  }

  const isAdvancedFilterApplied = hasAdvancedFilterChildren(advancedFilters);

  return (
    <>
      <FilterContainer>
        <Box>
          <Tooltip title={t("dimension")}>
            <IconButton
              size="small"
              onClick={changeSortDimen}
            >
              <Transform />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DpVersionSelect
            selectedVersion={setSelectVersion}
            reduxVersion={reduxViewState?.selectVersion ?? null}
            reload={vListReload}
            setReload={setVListReload}
          />
          <Box sx={{ display: isAdvancedFilterApplied ? "flex" : "none", width: "100%", alignItems: "center" }}>
            <BucketToggleButton value={selectTDCombo} onChange={handleChangeTimeBucket} buckets={[buckets.WEEK, buckets.MONTH, buckets.QUARTER]} />
            <ResetButton onClick={clickReset} />
          </Box>
        </Box>
        <Box sx={{ display: isAdvancedFilterApplied ? "none" : "flex", alignItems: "center", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  key={codeUtil.isWeekBucket(selectTD.current) ? "weekStart" : "monthStart"}
                  label={t("periodStart")}
                  views={codeUtil.isWeekBucket(selectTD.current) ? ["year", "month", "day"] : ["year", "month"]}
                  format={codeUtil.isWeekBucket(selectTD.current) ? "yyyy-MM-dd" : "yyyy-MM"}
                  minDate={MIN_START_DATE}
                  maxDate={MAX_START_DATE}
                  value={filterStartDate}
                  openTo={"month"}
                  shouldDisableMonth={(e) => WorkbenchUtil.ableMonthOfQuerter(e, selectTD.current)}
                  shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, selectTD.current, startDay.current)}
                  onAccept={newStartValue => {
                    newStartValue ? setFilterStartDate(newStartValue) : setFilterStartDate(filterStartDate);
                    requestReload();
                  }}
                  slotProps={{ textField: { InputProps: { size: "small", sx: { mr: 1, width: codeUtil.isWeekBucket(selectTDCombo) ? 150 : 130 } } } }}
                />
                <DatePicker
                  key={codeUtil.isWeekBucket(selectTD.current) ? "weekEnd" : "monthEnd"}
                  label={t("periodEnd")}
                  views={codeUtil.isWeekBucket(selectTDCombo) ? ["year", "month", "day"] : ["year", "month"]}
                  format={codeUtil.isWeekBucket(selectTDCombo) ? "yyyy-MM-dd" : "yyyy-MM"}
                  minDate={filterStartDate}
                  maxDate={MAX_END_DATE}
                  value={filterEndDate}
                  openTo={"month"}
                  shouldDisableMonth={(e) => WorkbenchUtil.ableEndMonthOfQuerter(e, selectTD.current)}
                  shouldDisableDate={(e) => WorkbenchUtil.ableEndDayOfWeek(e, selectTD.current, startDay.current)}
                  onAccept={newEndValue => {
                    newEndValue ? setFilterEndDate(newEndValue) : setFilterEndDate(filterEndDate);
                    requestReload();
                  }}
                  slotProps={{ textField: { InputProps: { size: "small", sx: { mr: 1, width: codeUtil.isWeekBucket(selectTDCombo) ? 150 : 130 } } } }}
                />
              </LocalizationProvider>
              <BucketToggleButton value={selectTDCombo} onChange={handleChangeTimeBucket} buckets={[buckets.WEEK, buckets.MONTH, buckets.QUARTER]} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title={t("hrchyGrp")} >
                <AccountTreeSharp
                  size="small"
                  variant="contained"
                  color= {muicolor.DEFAULT}
                  sx={{ cursor: "pointer" }}
                  onClick={filterActionHrchyGroup}
                  >
                </AccountTreeSharp>
              </Tooltip>
              <HrchyGroupModal
                open={hrchyGroupOpen}
                setOpen={setHrchyGroupOpen}
                anchorEl={filterEl}
                setAnchorEl={setFilterEl}
                custGrpLevel={custGrpLevel}
                itemGrpLevel={itemGrpLevel}
                selectCHG={selectCHG}
                selectIHG={selectIHG}
                close={setHrchyGroup}
              />
              <CustomerHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} defaultValue={defaultValue} customValue={customValue} enableSelectParent unuseClearIcon enableMultiSelect treeload={treeload} styles={{ width: 290, ml: 0.5 }} hrchyNumFilter={selectedHrchyNum}/>
              <IconButton
                aria-describedby={"custHrchyPop"}
                size="small"
                sx={{ mr: 1 }}
                onClick={filterActionCustomer}
                disabled={selectCL === ""}
                color={customerFilterColor}>
                <Tooltip title={t("customerFilter")}>
                  <FilterAlt />
                </Tooltip>
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} defaultValue={itemHrchyDefaultValue} customValue={itemCustomValue} enableSelectParent unuseClearIcon enableMultiSelect treeload={itemTreeLoad} styles={{ width: 290 }} hrchyNumFilter={selectedItemHrchyNum}/>
              <IconButton
                aria-describedby={"prodHrchyPop"}
                size="small"
                sx={{ mr: 1 }}
                onClick={filterActionItem}
                disabled={selectIL === ""}
                color={itemFilterColor}>
                <Tooltip title={t("itemFilter")}>
                  <FilterAlt />
                </Tooltip>
              </IconButton>
            </Box>
            <AbcXyzFilterModal viewName={viewName} invClassCdList={invClassCdList} setInvClassCdList={setInvClassCdList} disabled={disabledInvClass} />
            <FormControlLabel
              control={<Checkbox color="primary" name={"excludeEos"} checked={excludeEos} onChange={handleCheckbox} />}
              label={t("excludeEos")} key="EXCLUDE_EOS" sx={{ display: "flex" }} />
            <Box>
              <ResetButton onClick={clickReset} />
            </Box>
        </Box>
        <Box id="dp-workbench-button" sx={{ display: "flex", ml: "auto", mr: 1, alignItems: "center" }}>
          {disabledSave ? (
            <Box sx={{ mr: 1 }}>
              <AggregationGifIcon />
            </Box>
          ) : (
            <Box></Box>
          )}
          <SaveButton
            disabled={disabledSave}
            onClick={() => {
              gridApi.stopEditing(false);
              setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
            }}
          />
          {accessControl.current && approvalAccessControl.current && isEqual(selectVersion?.statusCd, dpVersion.SCENARIO) ? (
            <CheckButton
              label="confirm"
              disabled={!statusReady.current || selectVersion.parent !== null}
              onClick={() => {
                gridApi.stopEditing(false);
                setDialogInfo({ open: true, title: "confirm", content: t("MSG_VersionConfirm") });
              }}
            />
          ) : null }
          <Tooltip title={t("layout")}>
            <IconButton
              size="small"
              color={layoutOpen || (appliedLayout && size(appliedLayout) > 0) ? muicolor.INFO : muicolor.DEFAULT}
              onClick={handleLayoutOpen}>
              <ViewComfySharp />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("notes")}>
            <IconButton
              size="small"
              color="info"
              onClick={handleNoteModal}>
              <EditNote />
            </IconButton>
          </Tooltip>
          <DpExcelButtons
            gridApi={gridApi}
            gridRef={gridRef}
            responseRef={responseRef.current}
            columnDefs={GridUtils.getColumnDefs(columnDefs)}
            editableMeasure={editableMeasure.current}
            params={selectVersion ? getRequestParams() : null}
            saveData={setImportDatas}
            editable={WorkbenchEditable.isAllEditable(selectVersion, selectTD.current)}
            filter={{
              version: selectVersion,
            }}
            translationKey={translationKey}
            isChannel={isChannel}
          />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      {/*
       * 우측 패널(Note / Advanced Filter)은 SplitterContainer **바깥**에 둔다.
       *   안에 두면 위쪽 pane(그리드) 높이까지만 오고, 아래 차트 pane 옆은 빈 채로 남는다.
       *   여기 두면 셋 다 grid-area 전체 높이를 쓴다.
       */}
      <Box
        id={"dp-workbench"}
        className="grid-area"
        sx={{ display: "flex", width: "100%" }}>
        <Box sx={{ flexGrow: 1, minWidth: 0, height: "100%" }}>
          <SplitterContainer initialSizes={[7, 4]}>
            {/* 그리드와 페이지네이션이 한 칸 — 그리드만 안에서 페이지네이션 50px 을 뺀다 */}
            <Box className="ag-theme-balham" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Backdrop
                sx={{ color: "#fff", zIndex: theme => theme.zIndex.drawer + 1 }}
                open={finishSave}>
                <CircularProgress color="inherit" />
              </Backdrop>
              <Box sx={{ height: "calc(100% - 50px)", mb: 1 }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={workbench}
                  {...defaultGridSettingMemo}
                  onGridReady={onGridReady}
                  groupDefaultExpanded={-1}
                  groupHideOpenParents
                  groupDisplayType={"custom"}
                  context={
                    {
                      hasAdvancedFilter: isAdvancedFilterSent(advancedFilters)
                        && !(isEqual(resultCustLevelCd.current, customer.cd) && isEqual(resultItemLevelCd.current, item.cd)),
                     workbenchThreshold: workbenchThreshold.current,
                     headerList: formatGridHeader,
                     dataMap: dataMap.current
                   }
                  }
                  onCellValueChanged={onCellValueChanged}
                  onCellClicked={onCellClicked}
                  allowContextMenuWithControlKey
                  getContextMenuItems={getContextMenuItems}
                  suppressLastEmptyLineOnPaste
                  statusBar={statusBar}
                  excelStyles={WorkbenchUtil.excelStyles}
                  overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
                  suppressDragLeaveHidesColumns
                  onSortChanged={params => settingSortParams(params)}
                  suppressMultiSort
                  enableRowPinning
                  isRowPinnable={() => true}
                  grandTotalRow={undefined}
                />
              </Box>
              <PaginationContainer
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                pageSizes={pageSizes}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
              />
            </Box>
            <Box className="chart-grid-area chart-area" sx={{ height: "100%" }}>
              <Box id="dp-workbench-config" sx={{ position: "absolute", right: "0%", top: "1%", zIndex: 99}}>
                <SettingsButton
                  label="dpChartConfig"
                  onClick={handleChartConfigOpen}
                />
              </Box>
              <Box id={"chart-area"} sx={{ height: "100%" }}>{size(chartData) > 0 && chartSeries.length > 0 ?
                <ChartComposed
                  xAxis={["baseDate"]}
                  data={chartData}
                  dataSeries={chartSeries}
                  tooltipAnimation={false}
                  options={{ minHeight: "200px" }}
                  showConfigurationPopup={false}
                /> : null}</Box>
            </Box>
          </SplitterContainer>
        </Box>
        {/*
          * 우측 패널 3종은 같은 줄에 나란히 선다 — 툴바 버튼 순서(사용자 정의 → 노트 → 고급 필터)와 같게.
          * 각자 감싸는 Box 를 두지 않는다: 패널이 자기 폭 조절 손잡이를 **형제로** 내놓기 때문에
          * (SidePanel resizable) 감싸면 손잡이가 세로로 떨어지고, 폭 상한(부모 행의 70%)도 깨진다.
          * 패널 사이 간격은 손잡이의 좌우 여백이 겸한다.
          */}
        <LayoutListModal
          open={layoutOpen}
          close={setLayoutOpen}
          pageCd={viewName}
          modalResultData={setLayoutModalData}
          activeMeasure={measure}
          reduxViewState={reduxViewState}
        />
        {!workbenchNoteOpen ? (
          <NotesModal
            version={selectVersion}
            timeDimension={selectTD}
            cellData={workbenchCellData}
            setUpdateCellData={setUpdateNoteData}
            updateFlg={setNoteUpdateFlg}
            pageCd={viewName}
            close={handleNoteModal}
          />
        ) : null}
        {openFilter && (
          <>
            {/* 시안(PlanNEL Side Panels 1b)의 떠 있는 카드 — 각진 테두리 대신 라운드 + 그림자 */}
            <ResizableFilterPanel>
               <AdvancedFilter
                 columnDefs={advancedFilterColumnDefs}
                 advancedFilters={advancedFilters}
                 onApply={handleAdvancedFilterApply}
                 onRemoveAll={handleAdvancedFilterRemoveAll}
                 viewName={viewName}
                  dataMap={{
                    levels: grpLevel,
                    startDate: filterStartDate,
                    endDate: filterEndDate,
                    initialConditions: advancedFilterInitialConditions,
                    custLevelCd: selectCL,
                    itemLevelCd: selectIL,
                    /* 상단 레벨이 그룹 2 이상이어도 같은 그룹으로 넘긴다(없으면 AF 가 그룹 1 로 본다, TC-50) */
                    custHrchyNum: getGridHrchyNum(true),
                    itemHrchyNum: getGridHrchyNum(false),
                    timeDimension: selectTDCombo
                  }}
                 lockedFilterNames={ADVANCED_FILTER_LOCKED_NAMES}
                 timeDimension={selectTDCombo}
                 onClose={collapseFilter}
                 isFilterOpen={true}
               />
            </ResizableFilterPanel>
          </>
        )}
      </Box>
      <MeasureModal
        open={modalOpen}
        close={modalClose}
        displayModButtons
        data={modalData}
        dimensionAttrData={dimensionAttrData}
        modalResultData={setModalData}
        setDimensionAttrData={setDimensionAttrData}
        filter={{
          version: selectVersion,
          custLevel,
          itemLevel,
        }}
        isChannel={isChannel}
      />
      <BulkValueUpdateModal
        open={bulkValueUpdateModalOpen}
        close={handleBulkValueClose}
        displayModButtons
        selectVersion={selectVersion}
        nodeData={bulkValueNodeData}
        modalResultData={setBulkValueNodeData}
      />
      <DPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={modelCode}
        modalResultData={setFilterData}
        filterTypes={{ Customer: custFilterType, Item: itemFilterType }}
        filteredList={{ Customer: customerFilterList, Item: itemFilterList }}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <ChartConfigModal
        open={chartConfigOpen}
        close={setChartConfigOpen}
        content={chartP13nData}
        modalResultData={setChartModalResultData}
        editableMeasure={editableMeasure.current}
        additionalMeasures={isChannel ? chartAdditionalMeasures : []}
        viewName={viewName}
      />
      <DimensAttrModal
        open={dimenModalOpen}
        close={handleDimenClose}
        dimensionAttrData={dimensionAttrData}
        setDimensionAttrData={setDimensionAttrData}
        selectLv={dimenSelectLv}
        attrConfig={attrConfig}
      />
    </>
  );
};

export default withTranslation()(Workbench);




